import css from './styles.css';
import { streamChat } from './sse';
import { debounceMsFor } from './debounce';
import { renderTextWithTelLinks } from './render';
import { resolveReply } from '../lib/reply-actions';
import { makeT, guidePagesFor, type WT, type WLang, type WKey } from './i18n';
import { createStageLine } from './stage-line';
import { buildResumeMarker, readResumeMarker } from './resume';
import { MARK_PATHS, MARK_VIEWBOX } from '../lib/brand-mark';

/**
 * 어드민 '데이터 질의' 브리지의 계약. **구현은 이 번들에 없다** — 어드민 페이지가
 * window.__staytoqAdminAsk 로 등록하고, 거기서 인증된 지표 API 를 부른다.
 * 위젯은 질문을 넘기고 결과를 그릴 뿐이라 손님 번들에는 권한이 생기지 않는다.
 */
type AdminAskFn = (question: string) => Promise<{
  matched: boolean;
  answer?: string;
  href?: string;
  linkLabel?: string;
} | null>;

/* ─────────────────────────────────────────────────────────────────────────
   STAYTOQ 마크 — Q 컷(2026-10-10, 그 전엔 '고리와 점'). 모든 업체 손님 화면이 같은 마크를 받는다.

   2026-09-17 에 마케팅 마크를 '말풍선 + TOQ' 에서 '고리와 점'으로 바꾼 뒤 한동안은 우리 사이트만
   CSS 플래그(`--cbsa-mark: ring`)로 켜고 업체는 옛 TOQ 글자로 두었다. 2026-10-04 에 업체까지
   옮기면서 TOQ 분기와 플래그를 걷었다 — 두 길을 남겨 두면 한 위젯 안에 두 아이덴티티가 뜰 틈이 남는다.
   ───────────────────────────────────────────────────────────────────────── */
const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * `:root` 에 선언된 CSS 플래그를 **한 번만** 읽는다.
 *
 * 선언이 없으면 빈 문자열이고, 부르는 쪽은 전부 "빈 값 = 지금 모양"으로 읽는다 —
 * 읽기에 실패해도(예외 포함) 마찬가지다. 남의 화면을 안 바꾸는 쪽이 안전한 실패다.
 */
const cssFlags: Record<string, string> = {};
function cssFlag(name: string): string {
  if (!(name in cssFlags)) {
    let v = '';
    try { v = getComputedStyle(document.documentElement).getPropertyValue(name).trim(); }
    catch { /* noop */ }
    cssFlags[name] = v;
  }
  return cssFlags[name];
}

/** 브랜드 마크(Q 컷). 도형은 `src/lib/brand-mark.ts` 하나에서 온다 — 마케팅 로고와 같은 문자열. */
function brandMark(): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', MARK_VIEWBOX);
  svg.setAttribute('aria-hidden', 'true');
  for (const d of MARK_PATHS) {
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', d);
    svg.appendChild(path);
  }
  return svg;
}

/**
 * 마크 자리 하나를 채운다. **다섯 곳이 이 함수 하나를 쓴다** — 런처·접힌 런처·
 * 패널 헤더·'이용중' 배지·캐치프레이즈 워터마크. 한 곳만 바꾸면 한 위젯 안에
 * 두 개의 아이덴티티가 동시에 뜬다.
 */
function paintMark(el: HTMLElement): void {
  el.classList.add('cbsa-mark-ring');
  el.appendChild(brandMark());
}

/**
 * 업체 브랜드 자리(펼친 런처·접힌 런처·히어로 워터마크)를 채운다 — 업체가 `widget.iconUrl` 을 정했으면 그 이미지, 아니면 STAYTOQ 마크.
 *
 * 문서(docs/TENANT-CONFIG.md `widget.iconUrl` "런처 버튼 커스텀 이미지")와 스타일(`.cbsa-icon-img`)은
 * 남아 있었는데 이 분기가 빠져 있어 **설정해도 런처는 늘 우리 마크** 였다(2026-09-25 sjsystem.ai.kr 에서 발견 —
 * 회사 사이트에 우리 제품 로고가 떴다). 헤더는 원래 iconUrl 을 쓴다. 배지·워터마크는 'STAYTOQ 이용중'
 * 표시라 계속 마크를 쓴다.
 *
 * https 만 받는다 — 손님 페이지에 뜨는 이미지라 http 는 혼합 콘텐츠로 막히거나 경고가 뜬다.
 * 이미지가 깨지면 마크로 되돌린다(빈 버튼이 남지 않게).
 */
function paintLauncher(el: HTMLElement, iconUrl: string | undefined, imgClass: string): void {
  if (iconUrl && /^https:\/\//i.test(iconUrl)) {
    const img = document.createElement('img');
    img.className = imgClass;
    img.src = iconUrl;
    img.alt = '';
    img.decoding = 'async';
    img.onerror = () => { img.remove(); el.classList.remove('cbsa-icon-custom'); paintMark(el); };
    el.classList.add('cbsa-icon-custom');
    el.appendChild(img);
    return;
  }
  paintMark(el);
}

/** 답변 아래 '자세히 보기' 링크. innerHTML 금지(규칙 3) — DOM API 로만 만든다. */
function appendMoreLink(bubble: HTMLElement, href: string, label: string): void {
  const a = document.createElement('a');
  a.href = href;
  a.textContent = label;
  a.style.cssText = 'display:inline-block;margin-top:8px;font-size:12px;font-weight:700;text-decoration:underline;color:inherit;opacity:.75';
  bubble.appendChild(document.createElement('br'));
  bubble.appendChild(a);
}

// 예약 버튼·사장님 연결 판정은 채널 공용 규격에 있다(src/lib/reply-actions.ts).
// 테스트와 옛 호출부를 위해 이름은 그대로 내보낸다.
export { findSameTargetUrls } from '../lib/reply-actions';
export function shouldShowCta(
  info: { reservationUrl?: string; ctaForce?: boolean },
  deliveredUrlCount: number,
): boolean {
  if (!info.reservationUrl) return false;
  return deliveredUrlCount > 0 || info.ctaForce === true;
}

function isWLang(v: unknown): v is WLang {
  return v === 'ko' || v === 'en' || v === 'ja' || v === 'zh';
}

type IconName = 'chat' | 'leaf' | 'tent' | 'sparkle';

/* 카테고리 아이콘 SVG path 맵 (src/lib/category-icons.ts 와 동기화) */
const CAT_ICON_PATHS: Record<string, string> = {
  bed: 'M2 4v16M2 8h18a2 2 0 0 1 2 2v10M2 17h20M6 8v9M9.5 8a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z',
  calendar: 'M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z',
  clock: 'M12 6v6l4 2M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z',
  users: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75',
  'map-pin': 'M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0zM12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
  utensils: 'M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2M7 2v20M21 15V2v0a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3zm0 0v7',
  waves: 'M2 6c.6.5 1.2 1 2.5 1C7 7 7 5 9.5 5c2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1M2 12c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2s2.4 2 5 2c2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1M2 18c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2s2.4 2 5 2c2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1',
  tent: 'M3.5 21 14 3M20.5 21 10 3M4 21h16M12 3v18',
  car: 'M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10H8s-2.7.6-4.5 1.1C2.7 11.3 2 12.1 2 13v3c0 .6.4 1 1 1h2M7 17a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM17 17a2 2 0 1 0 0 4 2 2 0 0 0 0-4z',
  'credit-card': 'M1 10h22M3 4h18a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z',
  phone: 'M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.1-8.7A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.8.4 1.6.7 2.4a2 2 0 0 1-.4 2.1L8.1 9.5a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.8.3 1.6.5 2.4.7a2 2 0 0 1 1.7 2z',
  star: 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z',
  heart: 'M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1 1.1L12 21.3l7.8-7.8 1-1.1a5.5 5.5 0 0 0 0-7.8z',
  sun: 'M12 1v2M12 21v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M1 12h2M21 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10z',
  moon: 'M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8z',
  tree: 'M12 22v-7M17 8l4 4H3l4-4M14 4l3 4H7l3-4M12 2l2 2H10l2-2z',
  dog: 'M10 5.2A2.6 2.6 0 0 1 7.5 8 2.5 2.5 0 0 1 5 5.5V5a2 2 0 0 1 4-.5M14 5.2A2.6 2.6 0 0 0 16.5 8 2.5 2.5 0 0 0 19 5.5V5a2 2 0 0 0-4-.5M8 14v.5M16 14v.5M11.3 16a2 2 0 0 0 1.4 0M4.4 12a8 8 0 0 1 15.2 0 1 1 0 0 1-1 1.1H5.4A1 1 0 0 1 4.4 12zM12 13v9',
  baby: 'M9 12h.01M15 12h.01M10 16c.5.3 1.2.5 2 .5s1.5-.2 2-.5M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z',
  gift: 'M20 12v10H4V12M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 1 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z',
  info: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 16v-4M12 8h.01',
};

/* 위젯 UI 아이콘 — 메뉴처럼 **기능**을 가리키는 자리. 경로는 Lucide 기하.
   `CAT_ICON_PATHS` 와 따로 두는 이유: 그 맵은 `src/lib/category-icons.ts` 와
   **동기화 계약**이 걸린 목록이라, UI 아이콘을 섞으면 그 대조가 무너진다.

   예전엔 이 자리가 이모지였다(📖🔄🔠🔒). 이모지는 **기기마다 다른 그림**이고
   (애플·안드로이드·윈도가 각각 그린다) 굵기·색·크기를 우리가 못 정한다 —
   글자색을 따라가지도 않아 메뉴 글자와 따로 논다. 선 아이콘은 `currentColor` 라
   글자와 같은 색으로 늙는다. CLAUDE.md §7-10 이 어드민에 건 규칙과 같은 이유다. */
const UI_ICON_PATHS: Record<string, string[]> = {
  'book-open': [
    'M12 7v14',
    'M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z',
  ],
  'rotate-ccw': [
    'M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8',
    'M3 3v5h5',
  ],
  type: ['M12 4v16', 'M4 7V5a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v2', 'M9 20h6'],
  /* 아래 넷은 이용 가이드 4페이지의 **일러스트** 자리다(예전엔 64px 이모지).
     `moon` 은 `CAT_ICON_PATHS` 에도 거의 같은 모양이 있지만 가져다 쓰지 않는다 —
     그 맵은 category-icons.ts 와 묶여 있어, 거기서 빠지는 날 가이드가 조용히 빈다. */
  'messages-square': [
    'M14 9a2 2 0 0 1-2 2H6l-4 4V4c0-1.1.9-2 2-2h8a2 2 0 0 1 2 2z',
    'M18 9h2a2 2 0 0 1 2 2v11l-4-4h-6a2 2 0 0 1-2-2v-1',
  ],
  'mouse-pointer-click': [
    'm9 9 5 12 1.8-5.2L21 14Z',
    'M7.2 2.2 8 5.1', 'm5.1 8-2.9-.8', 'M14 4.1 12 6', 'm6 12-1.9 2',
  ],
  moon: ['M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8z'],
  'sliders-horizontal': [
    'M21 4h-7M10 4H3M21 12h-9M8 12H3M21 20h-5M12 20H3',
    'M14 2v4M8 10v4M16 18v4',
  ],
  'shield-check': [
    'M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z',
    'm9 12 2 2 4-4',
  ],
};

/**
 * 선 아이콘 하나.
 *
 * 굵기는 크기를 따라간다 — 17px 메뉴에서 2 는 획이 붙어 뭉치고(그래서 1.75),
 * 34px 일러스트에서 1.75 는 굵어 보인다(그래서 1.6). 같은 값을 두 크기에 쓰면
 * 한쪽은 반드시 어색하다.
 */
function makeUiIconSvg(key: keyof typeof UI_ICON_PATHS | string, size: number, stroke = 1.75): SVGSVGElement | null {
  const ds = UI_ICON_PATHS[key];
  if (!ds) return null;
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', String(stroke));
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.setAttribute('aria-hidden', 'true');
  for (const d of ds) {
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', d);
    svg.appendChild(path);
  }
  return svg;
}

// 손님 피드백용 엄지 아이콘 — Lucide thumbs-up / thumbs-down 경로를 **각각** 그린다.
// 예전엔 down 을 up 에 `<svg transform="scale(1,-1)">` 를 붙여 뒤집었는데, 바깥 svg 의 transform
// 속성은 크롬만 따르고 iOS Safari 는 무시해 아이폰에서 👍 가 둘로 보였다(2026-09-25 사장님 스크린샷).
const THUMB_PATHS: Record<'up' | 'down', [string, string]> = {
  up: ['M7 10v12', 'M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z'],
  down: ['M17 14V2', 'M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z'],
};
export function makeThumbSvg(up: boolean): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', '15');
  svg.setAttribute('height', '15');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  for (const d of THUMB_PATHS[up ? 'up' : 'down']) {
    const p = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    p.setAttribute('d', d);
    svg.appendChild(p);
  }
  return svg;
}

function makeCatIconSvg(key: string, size: number): SVGSVGElement | null {
  const d = CAT_ICON_PATHS[key];
  if (!d) return null;
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', d);
  svg.appendChild(path);
  return svg;
}

type PresetBlock =
  | { type: 'image'; url: string; alt?: string }
  | { type: 'text'; text: string }
  | {
      type: 'list';
      items: Array<{
        title: string;
        description?: string;
        thumbnail?: string;
        buttonLabel?: string;
        buttonUrl?: string;
      }>;
    }
  | {
      type: 'actions';
      items: Array<{
        label: string;
        url?: string;        // external/web link
        chipTrigger?: string; // sends as user message → RAG pipeline
        presetRef?: string;   // re-trigger another chip's preset by title
      }>;
    };

type SuggestedQuestion = string | {
  title: string;
  imageUrl?: string;
  description?: string;
  sendMessage?: string;
  // When set, clicking this item bypasses the LLM entirely and shows the
  // preset answer verbatim. Use for content that must be displayed exactly
  // as written (price tables, policies, contact info).
  presetAnswer?: string;
  presetImage?: string;
  // Rich multi-block reply (channel.io-style cards). When set, takes
  // precedence over presetAnswer/presetImage and renders block-by-block.
  presetBlocks?: PresetBlock[];
};

// 카테고리 카드(카드당 질문 3개) — 채널톡 스타일 캐러셀로 렌더.
type SuggestedGroup = { title: string; emoji?: string; questions: string[] };

type Config = {
  greeting: string;
  /** 업체 대표 전화(widget-config 가 내려준다). 미설정이면 undefined. */
  phone?: string;
  /** 사람 연결(config.handoff.enabled)이 켜졌나 — 입력창 위 안내 한 줄의 뒷부분을 정한다. */
  handoff?: boolean;

  suggestedQuestions: SuggestedQuestion[];
  suggestedGroups?: SuggestedGroup[];
  widget: {
    color: string;
    position: string;
    bottomOffset?: number;
    sideOffset?: number;
    icon?: IconName;
    iconUrl?: string;
    headerTitle?: string;
    headerSubtitle?: string;
    headerIconUrl?: string;
    /**
     * 헤더 위 캐치프라이즈. 업체가 설정에서 직접 넣는 한 줄이다(비우면 렌더 안 함).
     * 위젯을 여는 손님이 가장 먼저 보는 자리라 업체 브랜드가 온다.
     */
    catchphrase?: string;
    /**
     * 'band'(기본) = 헤더 위 한 줄 띠 · 'hero' = 쉼표로 줄을 나눈 큰 히어로.
     * 히어로는 대화 영역을 크게 먹어 요청한 업체에만 켠다.
     */
    catchphraseStyle?: 'band' | 'hero';
    expandedButton?: boolean;
    buttonLabel?: string;
    /** 위젯 UI 언어 강제('ko'|'en'). 미설정이면 navigator.language 로 자동 판정. */
    lang?: string;
  };
  name: string;
};

// 테마 색 위에 올라가는 글자/아이콘 색을 명도로 자동 결정.
// 밝은 배경(레몬옐로 등) → 진한 검정, 어두운 배경(기본 진초록) → 흰색.
function fgFor(bg: string): string {
  const m = bg.trim().match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!m) return 'white';
  let h = m[1];
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const L = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return L > 0.55 ? '#1a1a1a' : 'white';
}

export async function renderWidget(opts: {
  tenantSlug: string;
  color: string;
  position: string;
  apiBase: string;
  mode?: 'fixed' | 'inline';
  targetEl?: HTMLElement | null;
  /** 런처를 접을 수 있게 한다(어드민 전용). 손님 위젯은 끈 채로 둔다. */
  collapsible?: boolean;
  /**
   * 어드민 '데이터 질의' 브리지(어드민 전용). 켜져도 이 번들은 **인증을 다루지 않는다** —
   * 어드민 페이지가 등록한 window.__staytoqAdminAsk 훅에 질문을 넘길 뿐이다.
   * 손님 사이트에는 그 훅이 없어 아래 분기가 통째로 건너뛰어진다(매출이 샐 경로 없음).
   */
  adminBridge?: boolean;
  /**
   * 페이지의 말(script `data-lang`) — 다국어 홈페이지의 영어·일본어·중국어 화면(2026-10-25).
   * 한국어가 아닌 화면이면 저장된 손님 선택·테넌트 강제보다 앞선다(영어 페이지에 한국어 위젯이 뜨지 않게).
   * 'ko' 나 빈 값이면 지금까지 그대로(저장된 선택 → 테넌트 강제 → 한국어).
   */
  pageLang?: string;
}) {
  let cfg: Config;
  // 위젯 UI 언어 — 저장된 손님 선택(localStorage) → config.widget.lang(테넌트 강제) → 'ko'(기본).
  // navigator 기반 자동판정은 제거(기본은 항상 한국어). 손님이 스위처로 바꾸면 기억한다.
  const langKey = `cbsa_lang_${opts.tenantSlug}`;
  let lang: WLang = 'ko';
  try {
    const saved = localStorage.getItem(langKey);
    if (isWLang(saved)) lang = saved;
  } catch { /* ignore */ }
  try {
    const r = await fetch(`${opts.apiBase}/api/widget-config?tenant=${encodeURIComponent(opts.tenantSlug)}`);
    cfg = await r.json();
  } catch {
    cfg = {
      greeting: '',
      suggestedQuestions: [],
      widget: { color: opts.color, position: opts.position },
      name: '',
    };
  }
  // 저장된 손님 선택이 없을 때만 테넌트 강제 override를 적용(기본은 ko).
  {
    let hasSaved = false;
    try { hasSaved = isWLang(localStorage.getItem(langKey)); } catch { /* ignore */ }
    if (!hasSaved) lang = isWLang(cfg.widget?.lang) ? cfg.widget!.lang as WLang : 'ko';
    if (isWLang(opts.pageLang) && opts.pageLang !== 'ko') lang = opts.pageLang;
  }
  let t: WT = makeT(lang);
  // 테넌트가 인사말/업체명을 안 준 경우의 기본값만 언어에 맞춘다(사장님이 채운 값은 원문 유지).
  if (!cfg.greeting) cfg.greeting = t('fallbackGreeting');
  if (!cfg.name) cfg.name = t('botName');

  // 최초 인사말 말풍선 참조 — 언어 전환 시 이 말풍선만 번역 교체(대화 나머지는 원문 유지).
  let greetingBubbleEl: HTMLDivElement | null = null;

  // ---- 손님 콘텐츠 번역 캐시 ----
  // 사장님이 쓴 인사말/추천질문 원문(한국어) → 손님 선택 언어. 서버 /api/widget-i18n
  // 로 한 번 호출해 받은 결과를 언어별 Map(원문→번역)으로 캐시 + localStorage 영속화.
  // ko는 항상 원문(identity). 번역 실패 시 원문 폴백 → 위젯이 절대 깨지지 않는다.
  const translationCache: Record<WLang, Map<string, string>> = {
    ko: new Map(), en: new Map(), ja: new Map(), zh: new Map(),
  };
  // v2(2026-10-25): 업체 이름 표기 통일(latinName)·헤더 제목 번역 — 옛 캐시의 「Yangju Jamjam」을 버린다
  function i18nCacheKey(l: WLang) { return `cbsa_i18n_v2_${opts.tenantSlug}_${l}`; }
  (['en', 'ja', 'zh'] as WLang[]).forEach((l) => {
    try {
      const raw = localStorage.getItem(i18nCacheKey(l));
      if (raw) {
        const obj = JSON.parse(raw) as Record<string, string>;
        for (const k in obj) if (typeof obj[k] === 'string') translationCache[l].set(k, obj[k]);
      }
    } catch { /* ignore */ }
  });
  function headerTitleSrc(): string {
    return (cfg.widget as any)?.headerTitle?.trim() || cfg.name;
  }
  // 현재 언어 기준 번역 조회. 미번역이면 원문 그대로.
  function tr(src: string): string {
    if (!src) return src;
    if (lang === 'ko') return src;
    return translationCache[lang].get(src) ?? src;
  }
  // 현재 화면에 필요한 손님 콘텐츠 원문 문자열을 모두 모은다(인사말+추천질문 제목/설명+그룹 제목).
  function collectSourceStrings(): string[] {
    const set = new Set<string>();
    const add = (s: unknown) => { if (typeof s === 'string' && s.trim()) set.add(s); };
    add(cfg.greeting);
    add(headerTitleSrc());
    if ((cfg.widget as any)?.headerSubtitle?.trim()) add((cfg.widget as any).headerSubtitle.trim());
    for (const q of cfg.suggestedQuestions ?? []) {
      if (typeof q === 'string') { add(q); continue; }
      add(q.title); add(q.description); add(q.sendMessage);
    }
    for (const g of cfg.suggestedGroups ?? []) {
      add(g?.title);
      for (const qq of g?.questions ?? []) add(qq);
    }
    return [...set];
  }
  // 부족한 원문만 서버에 번역 요청 → 캐시 채우고 영속화. 실패는 조용히 무시(원문 폴백).
  async function ensureTranslations(target: WLang): Promise<void> {
    if (target === 'ko') return;
    const cache = translationCache[target];
    const need = collectSourceStrings().filter((s) => !cache.has(s)).slice(0, 30);
    if (!need.length) return;
    try {
      const r = await fetch(`${opts.apiBase}/api/widget-i18n`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantSlug: opts.tenantSlug, lang: target, texts: need }),
      });
      const data = (await r.json()) as { texts?: unknown };
      const out = Array.isArray(data.texts) ? data.texts : [];
      need.forEach((src, i) => {
        const tx = out[i];
        if (typeof tx === 'string' && tx.trim() && tx !== src) cache.set(src, tx);
      });
      try {
        localStorage.setItem(i18nCacheKey(target), JSON.stringify(Object.fromEntries(cache)));
      } catch { /* ignore */ }
    } catch { /* 네트워크 실패 → 원문 폴백 */ }
  }

  const isInline = opts.mode === 'inline';
  const mountParent = isInline && opts.targetEl ? opts.targetEl : document.body;

  // Idempotency: if React Strict Mode (or a quick remount) leaves an existing
  // .cbsa-root inside this mount target, remove it before appending a fresh one.
  // Only scope the cleanup to the immediate target so other widgets stay intact.
  if (isInline) {
    mountParent.querySelectorAll(':scope > .cbsa-root').forEach(n => n.remove());
  }

  const root = document.createElement('div');
  // 흰 바탕 스킨은 우리 라우트만 켠다 — 업체는 헤더에 자기 색이 차는 지금 모양이 기본이다.
  // 런처·패널이 모두 이 root 안에 들어가므로 여기 한 번만 붙이면 된다.
  root.className = 'cbsa-root' + (isInline ? ' cbsa-inline' : '')
    + (cssFlag('--cbsa-skin') === 'light' ? ' cbsa-skin-light' : '');
  mountParent.appendChild(root);

  const style = document.createElement('style');
  style.textContent = css;
  document.head.appendChild(style);

  const visitorKey = 'cbsa_visitor_id';
  let visitorId = localStorage.getItem(visitorKey);
  if (!visitorId) {
    visitorId = crypto.randomUUID();
    localStorage.setItem(visitorKey, visitorId);
  }
  // Persist conversationId across reloads / panel-close cycles so a single
  // browser session keeps appending to one conversation row in admin logs.
  // 6h TTL keeps long sessions linked but resets after a clear gap.
  const convKey = `cbsa_conv_${opts.tenantSlug}`;
  const convTsKey = `${convKey}_ts`;
  const CONV_TTL_MS = 6 * 60 * 60 * 1000;
  let conversationId: string | null = null;
  const storedConv = localStorage.getItem(convKey);
  const storedTs = parseInt(localStorage.getItem(convTsKey) ?? '0', 10);
  if (storedConv && Date.now() - storedTs < CONV_TTL_MS) {
    conversationId = storedConv;
  }
  const history: { role: 'user' | 'assistant'; content: string }[] = [];

  const effectivePosition = (typeof cfg.widget?.position === 'string' && cfg.widget.position) || opts.position;
  const isLeft = effectivePosition === 'bottom-left';
  // Tenant config can lift the widget up if the host site already uses the
  // bottom corner (e.g. an existing Kakao chat button on yangjujamjam.com).
  const bottomOffset =
    typeof cfg.widget?.bottomOffset === 'number' ? cfg.widget.bottomOffset : 24;
  const sideOffset =
    typeof cfg.widget?.sideOffset === 'number' ? cfg.widget.sideOffset : 24;
  // 런처 버튼 크기 — config로 키울 수 있음(기본 56). 데모 위젯을 눈에 띄게.
  const launcherSizeCfg = (cfg.widget as { launcherSize?: unknown })?.launcherSize;
  const launcherSize = typeof launcherSizeCfg === 'number' ? launcherSizeCfg : 56;
  // 모바일 홈 인디케이터/브라우저 하단바와 겹치지 않도록 safe-area 반영
  const bottomCss = `calc(${bottomOffset}px + env(safe-area-inset-bottom, 0px))`;
  const positionStyle = isLeft
    ? `left:${sideOffset}px;bottom:${bottomCss}`
    : `right:${sideOffset}px;bottom:${bottomCss}`;
  // For the panel only: also pin top:16px so the panel grows downward from
  // a safe 16px margin instead of extending above the viewport when the
  // button bottomOffset is large (e.g. 96px to clear a Kakao chat button).
  const panelTop = 12;
  // 창은 런처 **위**에 뜬다 — 런처가 보인 채로 X(닫기)로 바뀐다(채널톡과 같은 문법, 2026-10-09).
  // 예전엔 창 아래 끝(bottomOffset − 8)이 런처를 덮어, 데스크톱에서 닫으려면 머리말의 작은 X 를
  // 찾아야 했다. 휴대폰(≤480px)은 CSS 가 전체 화면으로 덮어쓰므로 이 값과 무관하다.
  const panelBottom = isInline ? Math.max(12, bottomOffset - 8) : bottomOffset + launcherSize + 12;
  const panelBottomCss = `calc(${panelBottom}px + env(safe-area-inset-bottom, 0px))`;
  const panelPositionStyle = isLeft
    ? `left:${sideOffset}px;top:${panelTop}px;bottom:${panelBottomCss}`
    : `right:${sideOffset}px;top:${panelTop}px;bottom:${panelBottomCss}`;
  // Color precedence: tenant config (DB) > script tag dataset > built-in default.
  // The DB color is the source of truth — script tag color is only a fallback
  // for when widget-config fetch fails.
  const effectiveColor = (typeof cfg.widget?.color === 'string' && cfg.widget.color) || opts.color;
  const colorVar = `--cbsa-color:${effectiveColor};--cbsa-fg:${fgFor(effectiveColor)}`;

  // Toggle button — only used in fixed mode. In inline mode the panel is
  // always open and embedded in the target container.
  const button = isInline ? null : document.createElement('button');
  const NS = SVG_NS;
  // 언어 전환 시 갱신할 런처 라벨(기본값일 때만 — 사장님 buttonLabel은 원문 유지).
  let launcherLabelEl: HTMLElement | null = null;
  // 런처 글자 — 사장님이 정한 글자(예: 「문의」)가 한글이면 한국어일 때만 쓴다. 다른 말에선 그 말의 기본 글자
  // (영어 페이지에 「문의」가 뜨던 문제, 2026-10-25). 로마자 등 한글이 아닌 글자는 어느 말에서나 그대로.
  function launcherText(): string {
    const own = typeof cfg.widget?.buttonLabel === 'string' ? cfg.widget.buttonLabel : '';
    return own && (lang === 'ko' || !/[가-힣]/.test(own)) ? own : t('buttonLabel');
  }
  if (button) {
  button.className = 'cbsa-button';
  button.setAttribute('aria-label', t('openAria'));
  button.setAttribute('aria-expanded', 'false');
  button.style.cssText = `${positionStyle};${colorVar}`;
  if (launcherSize !== 56) {
    button.style.width = `${launcherSize}px`;
    button.style.height = `${launcherSize}px`;
  }

  // 브랜드 마크 아이콘
  const brandEl = document.createElement('span');
  brandEl.className = 'cbsa-icon-chat cbsa-icon-brand';
  paintLauncher(brandEl, cfg.widget?.iconUrl, 'cbsa-icon-img');

  // 확장형 버튼 (tenant config에서 제어)
  const expandedBtn = cfg.widget?.expandedButton === true;
  const labelEl = document.createElement('span');
  labelEl.className = 'cbsa-icon-chat cbsa-button-label';
  labelEl.textContent = launcherText();
  launcherLabelEl = labelEl;
  if (expandedBtn) button.classList.add('cbsa-expanded');

  // Close icon: X
  const closeSvg = document.createElementNS(NS, 'svg');
  closeSvg.setAttribute('class', 'cbsa-icon-close');
  closeSvg.setAttribute('viewBox', '0 0 24 24');
  const x1 = document.createElementNS(NS, 'path');
  x1.setAttribute('d', 'M6 6l12 12M18 6l-12 12');
  closeSvg.appendChild(x1);
  if (expandedBtn) {
    button.append(brandEl, labelEl, closeSvg);
  } else {
    button.append(brandEl, closeSvg);
  }
  root.appendChild(button);
  }

  // ── 런처 접기 (data-collapsible 인 곳에서만) ────────────────────────────────
  //
  // 실측 2026-09-10 어드민 '예약 상담 문의' 화면 — 표가 화면 아래까지 차서 **마지막 행의
  // 상태 드롭다운이 런처에 깔렸다.** 사장님이 상태를 못 바꿨다. 런처는 z-index 가
  // 2147483000 이라 화면 쪽에서 이길 방법이 없다.
  //
  // 그래서 런처 자체에 접는 손잡이를 단다. 접으면 모서리에 붙는 작은 원(TOQ)만 남고,
  // 그걸 누르면 다시 커진다. 선택은 이 브라우저에 남는다 — 화면을 옮길 때마다 다시
  // 접게 하면 접는 의미가 없다.
  //
  // ⚠️ **손님 위젯에서는 켜지 않는다.** 이 버튼이 예약 문의의 입구라, 접을 수 있게
  // 만드는 순간 전환을 스스로 깎는다. 어드민 스크립트 태그만 data-collapsible 을 준다.
  const miniKey = `cbsa_mini_${opts.tenantSlug}`;
  let setMini: ((mini: boolean) => void) | null = null;
  if (button && opts.collapsible) {
    // 런처는 position:fixed 라 그 위에 손잡이를 얹으려면 자리를 잡아 줄 껍데기가 필요하다.
    // 껍데기는 접기를 켠 곳에서만 만든다(손님 위젯의 DOM 은 그대로 둔다).
    const wrap = document.createElement('div');
    wrap.className = 'cbsa-launch-wrap';
    wrap.style.cssText = positionStyle;
    root.appendChild(wrap);
    wrap.appendChild(button);
    // 자리는 이제 껍데기가 잡는다. 인라인으로 박아 둔 right/bottom 을 지우지 않으면
    // position:relative 인 버튼이 그 값만큼 **모서리 밖으로 밀려난다**(색 변수는 남긴다).
    button.style.right = '';
    button.style.left = '';
    button.style.bottom = '';

    const collapseBtn = document.createElement('button');
    collapseBtn.type = 'button';
    collapseBtn.className = 'cbsa-collapse' + (isLeft ? ' cbsa-collapse-left' : '');
    collapseBtn.setAttribute('aria-label', '문의 버튼 접기');
    collapseBtn.title = '접기';
    const minus = document.createElementNS(NS, 'svg');
    minus.setAttribute('viewBox', '0 0 24 24');
    const mPath = document.createElementNS(NS, 'path');
    mPath.setAttribute('d', 'M6 12h12');
    minus.appendChild(mPath);
    collapseBtn.appendChild(minus);
    wrap.appendChild(collapseBtn);

    const miniBtn = document.createElement('button');
    miniBtn.type = 'button';
    miniBtn.className = 'cbsa-mini';
    miniBtn.style.cssText = isLeft
      ? `left:${Math.max(8, sideOffset - 10)}px;bottom:calc(${Math.max(8, bottomOffset - 10)}px + env(safe-area-inset-bottom, 0px))`
      : `right:${Math.max(8, sideOffset - 10)}px;bottom:calc(${Math.max(8, bottomOffset - 10)}px + env(safe-area-inset-bottom, 0px))`;
    miniBtn.setAttribute('aria-label', '문의 버튼 펼치기');
    miniBtn.title = '문의 버튼 펼치기';
    paintLauncher(miniBtn, cfg.widget?.iconUrl, 'cbsa-mini-img');
    root.appendChild(miniBtn);

    setMini = (mini: boolean) => {
      wrap.style.display = mini ? 'none' : '';
      miniBtn.style.display = mini ? 'flex' : 'none';
    };
    // 직접 누른 것만 기억한다 — 예전엔 열 때마다 '0' 을 적어서, 기본값과 사람이 고른 값을 가를 수 없었다.
    const pick = (mini: boolean) => {
      setMini!(mini);
      try { localStorage.setItem(miniKey, mini ? '1' : '0'); localStorage.setItem(`${miniKey}_picked`, '1'); } catch { /* ignore */ }
    };
    collapseBtn.onclick = (e) => {
      e.stopPropagation();           // 런처 클릭(=패널 열기)으로 새지 않게
      setOpen(false);
      pick(true);
    };
    // 펼치기는 **펼치기만** 한다. 접어 둔 사람이 원한 건 자리를 비우는 것이지 대화가 아니다.
    miniBtn.onclick = () => pick(false);

    let wasMini = false;
    // 고른 적이 없으면 휴대폰에선 접어서 시작한다 — 큰 런처가 현황표 「다음날」·대시보드 「다음 기간」을 덮었다(2026-11-01).
    try {
      const saved = localStorage.getItem(miniKey);
      const picked = localStorage.getItem(`${miniKey}_picked`) === '1';
      wasMini = saved === '1' || (!picked && window.matchMedia('(max-width: 880px)').matches);
    } catch { /* ignore */ }
    setMini(wasMini);
  }

  // 채널톡식 선제안 말풍선(teaser) — config(widget.teasers)가 있을 때만, fixed 모드 전용.
  // 실고객 위젯은 teasers가 없어 아무것도 안 뜸. '?'로 끝나는 티저는 클릭 시 질문 전송,
  // 그 외는 패널만 오픈. 런처에는 빨간 알림 점(.cbsa-notify-dot)을 함께 표시.
  const teasers: string[] = Array.isArray((cfg.widget as { teasers?: unknown })?.teasers)
    ? ((cfg.widget as { teasers?: unknown[] }).teasers as unknown[]).filter(
        (t): t is string => typeof t === 'string',
      )
    : [];
  let teaserEl: HTMLDivElement | null = null;
  let teaserDot: HTMLSpanElement | null = null;
  function hideTeasers() {
    if (teaserEl) teaserEl.style.display = 'none';
    if (teaserDot) teaserDot.style.display = 'none';
  }
  if (button && teasers.length) {
    teaserDot = document.createElement('span');
    teaserDot.className = 'cbsa-notify-dot';
    button.appendChild(teaserDot);

    teaserEl = document.createElement('div');
    teaserEl.className = 'cbsa-teasers' + (isLeft ? ' cbsa-teasers-left' : '');
    const teaserBottom = bottomOffset + launcherSize + 14;
    const teaserBottomCss = `calc(${teaserBottom}px + env(safe-area-inset-bottom, 0px))`;
    teaserEl.style.cssText = isLeft
      ? `left:${sideOffset}px;bottom:${teaserBottomCss}`
      : `right:${sideOffset}px;bottom:${teaserBottomCss}`;
    // 방문자가 스크롤을 시작하면 티저 말풍선을 접어 콘텐츠(히어로 등)를 가리지 않게.
    // 알림 도트(cbsa-notify-dot)는 유지해 "새 메시지" 힌트는 남긴다.
    const collapseTeasers = () => {
      if (teaserEl) teaserEl.style.display = 'none';
      window.removeEventListener('scroll', onScrollCollapse);
    };
    const onScrollCollapse = () => { if (window.scrollY > 60) collapseTeasers(); };
    window.addEventListener('scroll', onScrollCollapse, { passive: true });
    for (const t of teasers) {
      const isQuestion = t.trim().endsWith('?');
      const bub = document.createElement('button');
      bub.type = 'button';
      bub.className = 'cbsa-teaser';
      bub.textContent = t;
      bub.onclick = () => {
        hideTeasers();
        setOpen(true);
        if (isQuestion) send(t);
      };
      teaserEl.appendChild(bub);
    }
    root.appendChild(teaserEl);
  }

  // Panel
  const panel = document.createElement('div');
  panel.className = 'cbsa-panel';
  panel.style.cssText = `${panelPositionStyle};${colorVar};display:none`;
  // 접근성 — 떠 있는 채팅창은 비모달 대화상자(페이지를 막지 않는다), 페이지에 박힌 것은 영역이다.
  // 이름(aria-label)은 헤더 제목이 만들어진 뒤 붙인다.
  panel.setAttribute('role', isInline ? 'region' : 'dialog');
  if (!isInline) { panel.setAttribute('aria-modal', 'false'); panel.tabIndex = -1; }

  if (isInline) {
    // Inline embed: panel is always visible, no toggle button to open it.
    panel.classList.add('is-open');
    panel.style.position = 'static';
    panel.style.height = '100%';
    panel.style.width = '100%';
    panel.style.maxHeight = 'none';
    panel.style.borderRadius = '14px';
    panel.style.boxShadow = 'none';
    panel.style.display = 'flex';
  }

  const header = document.createElement('div');
  header.className = 'cbsa-header';
  // Optional avatar (favicon/logo) on the left of the header.
  // headerIconUrl wins; otherwise falls back to iconUrl (the button icon)
  // so a single upload can cover both places if the operator wants.
  const headerAvatarUrl = cfg.widget?.headerIconUrl || cfg.widget?.iconUrl;
  if (headerAvatarUrl) {
    const avatar = document.createElement('img');
    avatar.className = 'cbsa-header-avatar';
    avatar.src = headerAvatarUrl;
    avatar.alt = '';
    avatar.loading = 'lazy';
    avatar.decoding = 'async';
    avatar.onerror = () => avatar.remove();
    header.appendChild(avatar);
  } else {
    const brandAv = document.createElement('span');
    brandAv.className = 'cbsa-header-brand';
    paintMark(brandAv);
    header.appendChild(brandAv);
  }
  const hText = document.createElement('div');
  hText.className = 'cbsa-header-text';
  const hN = document.createElement('strong');
  // 헤더 제목(업체 이름)도 손님 언어로 — 영어 화면에 「양주잼잼 글램핑&카라반」이 그대로 떴다
  hN.textContent = tr(headerTitleSrc());
  panel.setAttribute('aria-label', hN.textContent ?? '');
  const hG = document.createElement('small');
  // headerSubtitle wins; otherwise reuse the same greeting that appears as
  // the first assistant message so unconfigured tenants see something.
  // 표시 중인 값(부제 또는 인사말)을 손님 언어로 번역(tr). ko는 원문 그대로.
  const headerSubSrc = (cfg.widget as any)?.headerSubtitle?.trim() || cfg.greeting;
  const headerSubText = tr(headerSubSrc);
  // 부제가 전화번호면 눌러서 바로 걸 수 있게 한다. 모바일에서 헤더의 번호를
  // 눌러도 아무 일이 없던 게 이상했다. 숫자·+·-·괄호·공백만으로 이루어지고
  // 숫자가 8자리 이상일 때만 링크로 본다(주소·안내문구가 걸리지 않도록).
  const telDigits = headerSubText.replace(/[^\d+]/g, '');
  const looksLikePhone = /^[\d+()\-.\s]+$/.test(headerSubText) && telDigits.replace(/\D/g, '').length >= 8;
  if (looksLikePhone) {
    const telLink = document.createElement('a');
    telLink.className = 'cbsa-header-tel';
    telLink.href = `tel:${telDigits}`;
    telLink.textContent = headerSubText;
    hG.appendChild(telLink);
  } else {
    hG.textContent = headerSubText;
  }
  hText.append(hN, hG);

  const closeBtn = document.createElement('button');
  closeBtn.className = 'cbsa-close';
  closeBtn.setAttribute('aria-label', t('closeAria'));
  closeBtn.type = 'button';
  const closeSvgHeader = document.createElementNS(NS, 'svg');
  closeSvgHeader.setAttribute('viewBox', '0 0 24 24');
  const xPath = document.createElementNS(NS, 'path');
  xPath.setAttribute('d', 'M6 6l12 12M18 6l-12 12');
  closeSvgHeader.appendChild(xPath);
  closeBtn.appendChild(closeSvgHeader);

  // 언어 스위처 — X 버튼 왼쪽. 기본 한국어. 손님이 바꾸면 UI/인사말/추천질문을 해당 언어로.
  const langSel = document.createElement('select');
  langSel.className = 'cbsa-lang';
  langSel.setAttribute('aria-label', t('langLabel'));
  for (const [val, label] of [['ko', '한국어'], ['en', 'English'], ['ja', '日本語'], ['zh', '中文']] as const) {
    const o = document.createElement('option');
    o.value = val;
    o.textContent = label;
    langSel.appendChild(o);
  }
  langSel.value = lang;
  langSel.onchange = () => { void switchLang(langSel.value as WLang); };

  // "이전 대화" — 이 방문자의 과거 대화를 불러와 메시지 영역 위(과거)에 붙인다.
  // 클릭 시 loadPreviousConversation() 호출(아래 함수 선언, 호이스팅). 한 번 로드하면 버튼 제거.
  let historyLoaded = false;
  const historyBtn = document.createElement('button');
  // widget.mobileHistory === false 면 좁은 폭에서 이 버튼을 감춘다.
  // 헤더 폭이 모자라 상호·전화번호가 말줄임으로 잘리는 업체를 위한 선택지.
  // 값이 없으면 지금까지처럼 아이콘 버튼으로 그대로 보인다.
  const hideHistoryOnMobile = (cfg.widget as any)?.mobileHistory === false;
  historyBtn.className =
    'cbsa-history-btn' + (hideHistoryOnMobile ? ' cbsa-history-btn--mobile-off' : '');
  historyBtn.type = 'button';
  historyBtn.title = t('historyBtn');
  historyBtn.setAttribute('aria-label', t('historyBtn'));
  // 히스토리(되감기) 아이콘 — 좁은 폭에선 라벨을 숨기고 이 아이콘만 남긴다(아래 CSS).
  const historyIcon = document.createElementNS(NS, 'svg');
  historyIcon.setAttribute('viewBox', '0 0 24 24');
  for (const d of ['M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8', 'M3 3v5h5', 'M12 7v5l4 2']) {
    const pth = document.createElementNS(NS, 'path');
    pth.setAttribute('d', d);
    historyIcon.appendChild(pth);
  }
  const historyLabelEl = document.createElement('span');
  historyLabelEl.className = 'cbsa-history-label';
  historyLabelEl.textContent = t('historyBtn');
  historyBtn.append(historyIcon, historyLabelEl);
  historyBtn.onclick = () => { trackEvent('history_resume', { action: 'header_click' }); void loadPreviousConversation(); };

  header.append(hText, historyBtn, langSel, closeBtn);

  const msgs = document.createElement('div');
  msgs.className = 'cbsa-messages';

  // 스크린리더 알림 — 답변은 글자 단위로 흘러 들어오므로 대화 영역 자체를 live 로 두면
  // 조각마다 읽힌다. 다 받은 뒤 **완성된 답 한 번**만 이 숨은 영역으로 읽힌다.
  const liveEl = document.createElement('div');
  liveEl.className = 'cbsa-sr-only';
  liveEl.setAttribute('role', 'status');
  liveEl.setAttribute('aria-live', 'polite');
  liveEl.setAttribute('aria-atomic', 'true');
  function announce(el: HTMLElement) {
    // 비웠다 채워야 같은 답이 두 번 와도 다시 읽힌다. onDone 의 뒤처리(버튼·링크 정리)가
    // 끝난 뒤의 글을 읽도록 한 박자 늦춘다.
    setTimeout(() => {
      liveEl.textContent = '';
      setTimeout(() => { liveEl.textContent = (el.textContent ?? '').trim(); }, 30);
    }, 0);
  }

  // Suggested-questions area is now wrapped in a small section with a toggle
  // header so the user can collapse it to read assistant answers without the
  // chip strip eating screen space. Open/closed state persists per tenant.
  const suggSection = document.createElement('div');
  suggSection.className = 'cbsa-sugg-section';

  const suggToggle = document.createElement('button');
  suggToggle.className = 'cbsa-sugg-toggle';
  suggToggle.type = 'button';
  const suggCollapseKey = `cbsa_sugg_collapsed_${opts.tenantSlug}`;
  // Default-collapse the chip strip when there are >4 items so the message
  // area gets more room. Users explicitly toggling persists their choice.
  // 카테고리 그룹(카드당 3개). 유효 그룹이 있으면 캐러셀로 렌더하고
  // 기본은 펼친 상태(첫 카드 질문 3개가 바로 보이도록).
  const suggGroups: SuggestedGroup[] = (Array.isArray(cfg.suggestedGroups) ? cfg.suggestedGroups : [])
    .map(g => ({
      title: typeof g?.title === 'string' ? g.title : '',
      emoji: typeof g?.emoji === 'string' ? g.emoji : '',
      questions: (Array.isArray(g?.questions) ? g.questions : [])
        .filter(q => typeof q === 'string' && q.trim() !== '')
        .slice(0, 3),
    }))
    .filter(g => g.title.trim() !== '' && g.questions.length > 0);
  // 캐러셀(카드 헤더+질문3+도트)은 칩 strip보다 세로 공간이 더 필요 →
  // 섹션 max-height 캡을 키우는 클래스 부여(아래 CSS에서 처리).
  if (suggGroups.length) suggSection.classList.add('cbsa-has-grp');
  const suggStored = localStorage.getItem(suggCollapseKey);
  // 처음 연 손님에겐 **항상 펼친다** — 빈 입력창보다 질문 예시가 먼저 보여야 한다(2026-10-09).
  // 예전엔 칩이 5개 이상이고 묶음이 없으면 접혀서 나갔다. 손님이 직접 접은 선택(저장값)은 그대로 따른다.
  let suggCollapsed = suggStored === '1';

  const suggLabel = document.createElement('span');
  suggLabel.className = 'cbsa-sugg-label';
  const suggCaret = document.createElementNS(NS, 'svg');
  suggCaret.setAttribute('viewBox', '0 0 24 24');
  suggCaret.setAttribute('fill', 'none');
  suggCaret.setAttribute('stroke', 'currentColor');
  suggCaret.setAttribute('stroke-width', '2.5');
  suggCaret.setAttribute('stroke-linecap', 'round');
  suggCaret.setAttribute('stroke-linejoin', 'round');
  suggCaret.classList.add('cbsa-sugg-caret');
  const caretPath = document.createElementNS(NS, 'polyline');
  caretPath.setAttribute('points', '6 9 12 15 18 9');
  suggCaret.appendChild(caretPath);
  suggToggle.append(suggLabel, suggCaret);

  const sugg = document.createElement('div');
  sugg.className = 'cbsa-suggested';

  function applySuggCollapsed() {
    if (suggCollapsed) {
      sugg.style.display = 'none';
      suggLabel.textContent = t('suggShow', { n: suggGroups.length || (cfg.suggestedQuestions?.length ?? 0) });
      suggCaret.style.transform = 'rotate(180deg)';
    } else {
      sugg.style.display = '';
      suggLabel.textContent = t('suggHide');
      suggCaret.style.transform = 'rotate(0deg)';
    }
  }
  suggToggle.onclick = () => {
    suggCollapsed = !suggCollapsed;
    localStorage.setItem(suggCollapseKey, suggCollapsed ? '1' : '0');
    applySuggCollapsed();
  };
  applySuggCollapsed();

  // 스크롤 끝 도달 시 하단 페이드 숨김
  sugg.addEventListener('scroll', () => {
    const atEnd = sugg.scrollTop + sugg.clientHeight >= sugg.scrollHeight - 4;
    suggSection.classList.toggle('cbsa-scrolled-end', atEnd);
  });

  suggSection.append(suggToggle, sugg);

  const inputBar = document.createElement('div');
  inputBar.className = 'cbsa-input';

  // 메뉴 버튼 (그리드 아이콘) — 이용 가이드 등 부가 기능 팝업을 연다.
  const menuBtn = document.createElement('button');
  menuBtn.type = 'button';
  menuBtn.className = 'cbsa-menu-btn';
  menuBtn.setAttribute('aria-label', t('menuAria'));
  const menuSvg = document.createElementNS(NS, 'svg');
  menuSvg.setAttribute('viewBox', '0 0 24 24');
  for (const [x, y] of [[4, 4], [13, 4], [4, 13], [13, 13]] as const) {
    const r = document.createElementNS(NS, 'rect');
    r.setAttribute('x', String(x)); r.setAttribute('y', String(y));
    r.setAttribute('width', '7'); r.setAttribute('height', '7'); r.setAttribute('rx', '2');
    menuSvg.appendChild(r);
  }
  menuBtn.appendChild(menuSvg);

  const input = document.createElement('input');
  input.type = 'text';
  input.placeholder = t('inputPlaceholder');
  const sendBtn = document.createElement('button');
  sendBtn.textContent = t('send');

  // 메뉴 팝업
  const menuPop = document.createElement('div');
  menuPop.className = 'cbsa-menu-pop';
  menuPop.style.display = 'none';
  function closeMenu() { menuPop.style.display = 'none'; }
  function openMenu() { menuPop.style.display = 'block'; }
  menuBtn.onclick = (e) => {
    e.stopPropagation();
    menuPop.style.display === 'none' ? openMenu() : closeMenu();
  };
  document.addEventListener('click', (e) => {
    const t = e.target as Node;
    if (menuPop.style.display !== 'none' && !menuPop.contains(t) && !menuBtn.contains(t)) closeMenu();
  });

  // 글자 크기 (보통 / 크게 / 아주 크게) — 어르신 사장님·손님 배려.
  const FS_KEY = `cbsa_fs_${opts.tenantSlug}`;
  const FS_LABELS = [t('fsNormal'), t('fsLarge'), t('fsXLarge')];
  let fsIdx = parseInt(localStorage.getItem(FS_KEY) ?? '0', 10);
  if (!(fsIdx >= 0 && fsIdx <= 2)) fsIdx = 0;
  const fsLabelSpan = document.createElement('span');
  function applyFs() {
    panel.classList.remove('cbsa-fs-1', 'cbsa-fs-2');
    if (fsIdx === 1) panel.classList.add('cbsa-fs-1');
    if (fsIdx === 2) panel.classList.add('cbsa-fs-2');
    fsLabelSpan.textContent = `${t('fontSizeLabel')}: ${FS_LABELS[fsIdx]}`;
  }
  function cycleFs() {
    fsIdx = (fsIdx + 1) % 3;
    try { localStorage.setItem(FS_KEY, String(fsIdx)); } catch { /* ignore */ }
    applyFs();
  }

  // 대화 내용 초기화 — 화면/세션만 비움 (서버 로그는 보존됨).
  function resetConversation() {
    while (msgs.firstChild) msgs.removeChild(msgs.firstChild);
    history.length = 0;
    conversationId = null;
    try { localStorage.removeItem(convKey); localStorage.removeItem(convTsKey); } catch { /* ignore */ }
    greetingBubbleEl = appendMsg('assistant', tr(cfg.greeting));
  }

  /** 아이콘은 **키**로 받는다 — 경로는 `UI_ICON_PATHS` 한 곳에 있다. */
  function makeMenuItem(iconKey: string, labelEl: Node, onClick: () => void) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'cbsa-menu-item';
    const ic = document.createElement('span');
    ic.className = 'cbsa-menu-ic';
    const svg = makeUiIconSvg(iconKey, 17);
    if (svg) ic.appendChild(svg);
    b.append(ic, labelEl);
    b.onclick = onClick;
    return b;
  }
  const guideLabel = document.createElement('span');
  guideLabel.textContent = t('guide');
  const resetLabel = document.createElement('span');
  resetLabel.textContent = t('reset');
  const privacyLabel = document.createElement('span');
  privacyLabel.textContent = t('privacy');
  menuPop.append(
    makeMenuItem('book-open', guideLabel, () => { closeMenu(); openGuide(0); }),
    makeMenuItem('rotate-ccw', resetLabel, () => { closeMenu(); resetConversation(); }),
    makeMenuItem('type', fsLabelSpan, () => { cycleFs(); }),
    makeMenuItem('shield-check', privacyLabel, () => {
      closeMenu();
      window.open(`${opts.apiBase}/legal/privacy`, '_blank', 'noopener,noreferrer');
    }),
  );

  inputBar.append(menuBtn, input, sendBtn, menuPop);

  // ---- 이용 가이드 (페이지 캐러셀 오버레이) ----
  const guidePages = guidePagesFor(lang, cfg.name);

  const guide = document.createElement('div');
  guide.className = 'cbsa-guide';
  guide.style.display = 'none';

  const gHead = document.createElement('div');
  gHead.className = 'cbsa-guide-head';
  const gHTitle = document.createElement('span');
  gHTitle.className = 'cbsa-guide-htitle';
  gHTitle.textContent = t('guide');
  const gClose = document.createElement('button');
  gClose.type = 'button';
  gClose.className = 'cbsa-guide-close';
  gClose.setAttribute('aria-label', t('guideClose'));
  const gCloseSvg = document.createElementNS(NS, 'svg');
  gCloseSvg.setAttribute('viewBox', '0 0 24 24');
  const gClosePath = document.createElementNS(NS, 'path');
  gClosePath.setAttribute('d', 'M6 6l12 12M18 6l-12 12');
  gCloseSvg.appendChild(gClosePath);
  gClose.appendChild(gCloseSvg);
  gHead.append(gHTitle, gClose);

  const gBody = document.createElement('div');
  gBody.className = 'cbsa-guide-body';
  const gHeadline = document.createElement('h2');
  gHeadline.className = 'cbsa-guide-headline';
  const gArt = document.createElement('div');
  gArt.className = 'cbsa-guide-art';
  const gIcon = document.createElement('div');
  gIcon.className = 'cbsa-guide-ic';
  const gCap = document.createElement('div');
  gCap.className = 'cbsa-guide-cap';
  gArt.append(gIcon, gCap);
  gBody.append(gHeadline, gArt);

  const gFoot = document.createElement('div');
  gFoot.className = 'cbsa-guide-foot';
  const gSkip = document.createElement('button');
  gSkip.type = 'button';
  gSkip.className = 'cbsa-guide-skip';
  gSkip.textContent = t('skip');
  const gCenter = document.createElement('div');
  gCenter.className = 'cbsa-guide-center';
  const gDots = document.createElement('div');
  gDots.className = 'cbsa-guide-dots';
  const gPause = document.createElement('button');
  gPause.type = 'button';
  gPause.className = 'cbsa-guide-pause';
  gPause.setAttribute('aria-label', t('pauseAria'));
  gPause.textContent = '❚❚';
  gCenter.append(gDots, gPause);
  const gNext = document.createElement('button');
  gNext.type = 'button';
  gNext.className = 'cbsa-guide-next';
  gFoot.append(gSkip, gCenter, gNext);

  guide.append(gHead, gBody, gFoot);
  panel.appendChild(guide);

  let gIdx = 0;
  let gPaused = false;
  let gTimer: number | null = null;
  function stopAuto() { if (gTimer !== null) { clearInterval(gTimer); gTimer = null; } }
  function startAuto() {
    stopAuto();
    if (gPaused) return;
    gTimer = window.setInterval(() => {
      if (gIdx < guidePages.length - 1) { gIdx++; renderGuide(); }
      else stopAuto();
    }, 5000);
  }
  function renderGuide() {
    const p = guidePages[gIdx];
    gHeadline.replaceChildren();
    for (const s of p.segs) {
      const sp = document.createElement('span');
      if (s.hl) sp.className = 'cbsa-guide-hl';
      sp.textContent = s.t;
      gHeadline.appendChild(sp);
    }
    gIcon.replaceChildren();
    const pageIcon = makeUiIconSvg(p.icon, 34, 1.6);
    if (pageIcon) gIcon.appendChild(pageIcon);
    gCap.textContent = p.cap;
    gDots.replaceChildren();
    guidePages.forEach((_, i) => {
      const d = document.createElement('span');
      d.className = 'cbsa-guide-dot' + (i === gIdx ? ' is-on' : '');
      gDots.appendChild(d);
    });
    const last = gIdx === guidePages.length - 1;
    gSkip.style.visibility = last ? 'hidden' : 'visible';
    gNext.textContent = last ? t('start') : t('next');
  }
  function openGuide(i = 0) {
    gIdx = i;
    gPaused = false;
    gPause.textContent = '❚❚';
    renderGuide();
    guide.style.display = 'flex';
    startAuto();
  }
  function closeGuide() { stopAuto(); guide.style.display = 'none'; }
  gClose.onclick = closeGuide;
  gSkip.onclick = closeGuide;
  gNext.onclick = () => {
    stopAuto();
    if (gIdx < guidePages.length - 1) { gIdx++; renderGuide(); }
    else closeGuide();
  };
  gPause.onclick = () => {
    gPaused = !gPaused;
    if (gPaused) { stopAuto(); gPause.textContent = '▶'; }
    else { gPause.textContent = '❚❚'; startAuto(); }
  };
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (guide.style.display !== 'none') closeGuide();
    // 창 안에 포커스가 있을 때만 닫는다 — 업체 페이지의 Esc 를 가로채지 않는다.
    else if (!isInline && panel.style.display !== 'none' && panel.contains(document.activeElement)) setOpen(false);
  });

  applyFs();

  // "STAYTOQ 이용중" 배지
  const badge = document.createElement('a');
  badge.href = 'https://staytoq.com';
  badge.target = '_blank';
  badge.rel = 'noopener noreferrer';
  badge.className = 'cbsa-badge';
  const badgeIcon = document.createElement('span');
  badgeIcon.className = 'cbsa-badge-icon';
  paintMark(badgeIcon);
  badge.appendChild(badgeIcon);
  const badgeTextNode = document.createTextNode(t('badge'));
  badge.appendChild(badgeTextNode);

  // 헤더 위 캐치프라이즈 — 업체가 설정에 넣은 한 줄. 비어 있으면 아무것도 만들지 않는다.
  // 손님 언어로 번역하지 않는다: 브랜드 문구는 옮기면 뜻이 무너진다(예: "TALK. OPTIMIZE. QUICK RESPONSE.").
  const catchEls: HTMLElement[] = [];
  const catchText = (cfg.widget?.catchphrase ?? '').trim();
  if (catchText) {
    const isHero = cfg.widget?.catchphraseStyle === 'hero';
    const box = document.createElement('div');
    box.className = isHero ? 'cbsa-hero' : 'cbsa-catch';
    // 워터마크는 장식이라 스크린리더가 읽을 이유가 없다. 큰 히어로에서만 쓴다.
    if (isHero) {
      const mark = document.createElement('span');
      mark.className = 'cbsa-hero-mark';
      // 업체 로고가 있으면 워터마크도 그 로고 — 회사 사이트 히어로에 우리 제품 마크가 깔리지 않게(2026-09-25).
      paintLauncher(mark, cfg.widget?.iconUrl, 'cbsa-hero-img');
      mark.setAttribute('aria-hidden', 'true');
      box.appendChild(mark);
    }
    const copy = document.createElement('p');
    copy.className = isHero ? 'cbsa-hero-copy' : 'cbsa-catch-copy';
    if (isHero) {
      // 쉼표에서 줄을 나눈다 — 쉼표는 지우지 않는다("Ask," 처럼 남아야 리듬이 산다).
      // 마침표 뒤에 띄어쓰기가 오면 거기서도 나눈다(2026-10-11) — 서명 「TALK. OPTIMIZE. QUICK RESPONSE.」 가
      // 쉼표가 없어 폭 따라 「…QUICK / RESPONSE.」 로 꺾였다. 끝 마침표·'3.5' 같은 숫자는 뒤에 띄어쓰기가 없어 그대로다.
      // <br> 대신 요소로 쌓아 innerHTML 을 쓰지 않는다(CLAUDE.md §7-3).
      const lines = catchText.split(/(?<=,)\s*|(?<=\.)\s+/).map(x => x.trim()).filter(Boolean);
      for (const line of lines) {
        const sp = document.createElement('span');
        sp.textContent = line;
        copy.appendChild(sp);
      }
    } else {
      copy.textContent = catchText;
    }
    box.appendChild(copy);
    catchEls.push(box);
  }

  // 입력창 바로 위 안내 한 줄 — 누가 답하는지 묻기 전에 알린다. 사람 연결 기능은 이미 있는데
  // 손님은 그걸 미리 알 수 없었다(2026-10-09). 숙박이 아닌 업체도 쓰므로 '사장님'이 아니라 '담당자'.
  const statusLine = document.createElement('div');
  statusLine.className = 'cbsa-status';
  const statusDot = document.createElement('span');
  statusDot.className = 'cbsa-status-dot';
  statusDot.setAttribute('aria-hidden', 'true');
  const statusText = document.createElement('span');
  const paintStatus = () => {
    statusText.textContent = cfg.handoff ? `${t('statusAi')} · ${t('statusHandoff')}` : t('statusAi');
  };
  paintStatus();
  statusLine.append(statusDot, statusText);

  panel.append(...catchEls, header, msgs, suggSection, statusLine, inputBar, badge, liveEl);
  root.appendChild(panel);

  greetingBubbleEl = appendMsg('assistant', tr(cfg.greeting));

  // 다시 온 손님 — "지난 대화 이어보기" 카드(resume.ts). 인사말 위, 대화창 맨 위에 한 장.
  // 지금 이어지는 대화(6시간 안)가 있어도 띄운다: 새로고침하면 화면엔 인사말만 남아 앞 대화가 안 보인다.
  const resumeKey = `cbsa_last_${opts.tenantSlug}`;
  let resumeCard: HTMLDivElement | null = null;
  const resumeTitleEl = document.createElement('strong');
  const resumeBtnEl = document.createElement('button');
  const paintResume = () => {
    resumeTitleEl.textContent = t('resumeTitle');
    resumeBtnEl.textContent = t('resumeBtn');
  };
  {
    let marker = null as ReturnType<typeof readResumeMarker>;
    try { marker = readResumeMarker(localStorage.getItem(resumeKey), Date.now()); } catch { /* ignore */ }
    if (marker) {
      resumeCard = document.createElement('div');
      resumeCard.className = 'cbsa-resume';
      const body = document.createElement('div');
      body.className = 'cbsa-resume-body';
      const q = document.createElement('span');
      q.className = 'cbsa-resume-q';
      q.textContent = `“${marker.q}”`;
      body.append(resumeTitleEl, q);
      resumeBtnEl.type = 'button';
      resumeBtnEl.className = 'cbsa-resume-btn';
      paintResume();
      resumeBtnEl.onclick = () => {
        trackEvent('history_resume', { action: 'card_click' });
        resumeCard?.remove();
        resumeCard = null;
        void loadPreviousConversation();
      };
      resumeCard.append(body, resumeBtnEl);
      msgs.insertBefore(resumeCard, msgs.firstChild);
      trackEvent('history_resume', { action: 'card_view' });
    }
  }

  // Suggested questions: support both legacy string format and rich object
  // format ({ title, imageUrl?, description?, sendMessage? }). Strings render
  // as the simple chip above; rich objects with image or description render
  // as a vertical card. The two formats can be mixed in the same array.
  function isRich(q: SuggestedQuestion): q is Exclude<SuggestedQuestion, string> {
    return typeof q === 'object' && (!!q.imageUrl || !!q.description);
  }
  // 채널톡식 카테고리 카드 캐러셀 — 카드당 질문 최대 3개, 스와이프 +
  // 하단 도트. 그룹이 있으면 기존 평면 칩 대신 이걸 렌더한다.
  function renderSuggGroups() {
    const wrap = document.createElement('div');
    wrap.className = 'cbsa-grp-wrap';
    suggGroups.forEach(g => {
      const card = document.createElement('div');
      card.className = 'cbsa-grp-card';
      const head = document.createElement('div');
      head.className = 'cbsa-grp-head';
      if (g.emoji) {
        const iconSvg = makeCatIconSvg(g.emoji, 16);
        if (iconSvg) { iconSvg.style.verticalAlign = '-3px'; iconSvg.style.marginRight = '4px'; head.appendChild(iconSvg); }
        else { const em = document.createElement('span'); em.textContent = g.emoji + ' '; head.appendChild(em); }
      }
      head.appendChild(document.createTextNode(tr(g.title)));
      card.appendChild(head);
      g.questions.slice(0, 3).forEach(qq => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'cbsa-grp-q';
        b.textContent = tr(qq);
        // 프리셋 매칭: suggestedQuestions에 같은 title의 프리셋이 있으면 LLM 대신 사용
        const matched = (cfg.suggestedQuestions ?? []).find(
          (sq: SuggestedQuestion): sq is Exclude<SuggestedQuestion, string> =>
            typeof sq === 'object' && sq.title === qq && !!(sq.presetAnswer || sq.presetBlocks)
        );
        if (matched) {
          b.onclick = () => sendPreset(matched.title, {
            answer: matched.presetAnswer ?? '',
            image: matched.presetImage,
            blocks: matched.presetBlocks,
          });
        } else {
          // 비한국어면 번역된 질문을 보내 서버가 해당 언어로 답변하게 한다.
          b.onclick = () => send(tr(qq));
        }
        card.appendChild(b);
      });
      wrap.appendChild(card);
    });
    sugg.appendChild(wrap);
    if (suggGroups.length > 1) {
      let currentIdx = 0;
      const nav = document.createElement('div');
      nav.className = 'cbsa-grp-nav';

      const prevBtn = document.createElement('button');
      prevBtn.type = 'button';
      prevBtn.className = 'cbsa-grp-arrow prev';
      prevBtn.setAttribute('aria-label', t('prev'));
      prevBtn.textContent = '\u2039';

      const nextBtn = document.createElement('button');
      nextBtn.type = 'button';
      nextBtn.className = 'cbsa-grp-arrow next';
      nextBtn.setAttribute('aria-label', t('next'));
      nextBtn.textContent = '\u203A';

      const dots = document.createElement('div');
      dots.className = 'cbsa-dots';
      const dotEls = suggGroups.map((_, idx) => {
        const d = document.createElement('button');
        d.type = 'button';
        d.className = 'cbsa-dot' + (idx === 0 ? ' active' : '');
        d.setAttribute('aria-label', t('nthCategory', { n: idx + 1 }));
        d.onclick = () => goTo(idx);
        dots.appendChild(d);
        return d;
      });

      function goTo(idx: number) {
        const clamped = Math.max(0, Math.min(idx, suggGroups.length - 1));
        const w = document.querySelector('.cbsa-grp-wrap');
        if (!w) return;
        const target = w.children[clamped] as HTMLElement;
        if (!target) return;
        w.scrollTo({ left: target.offsetLeft - (w as HTMLElement).offsetLeft, behavior: 'auto' });
        // 즉시 인덱스 갱신 — scroll 이벤트 타이밍 의존 방지
        currentIdx = clamped;
        dotEls.forEach((d, k) => d.classList.toggle('active', k === clamped));
        prevBtn.classList.toggle('disabled', clamped === 0);
        nextBtn.classList.toggle('disabled', clamped === suggGroups.length - 1);
      }
      prevBtn.addEventListener('click', () => goTo(currentIdx + 1 > 0 ? currentIdx - 1 : 0));
      nextBtn.addEventListener('click', () => goTo(currentIdx + 1));

      const sync = () => {
        const w = document.querySelector('.cbsa-grp-wrap');
        if (!w) return;
        let best = 0, min = Infinity;
        for (let i = 0; i < w.children.length; i++) {
          const c = w.children[i] as HTMLElement;
          const dist = Math.abs((c.offsetLeft - (w as HTMLElement).offsetLeft) - w.scrollLeft);
          if (dist < min) { min = dist; best = i; }
        }
        currentIdx = best;
        dotEls.forEach((d, k) => d.classList.toggle('active', k === best));
        prevBtn.classList.toggle('disabled', best === 0);
        nextBtn.classList.toggle('disabled', best === suggGroups.length - 1);
      };
      wrap.addEventListener('scroll', sync, { passive: true });
      sync();

      nav.append(prevBtn, dots, nextBtn);
      sugg.appendChild(nav);
    }
  }
  // 추천질문 렌더(그룹 캐러셀 또는 평면 칩/카드). 언어 전환 시 재호출로 번역 반영.
  function renderSuggestions() {
    while (sugg.firstChild) sugg.removeChild(sugg.firstChild);
    if (suggGroups.length) { renderSuggGroups(); return; }
    for (const q of cfg.suggestedQuestions ?? []) {
      if (typeof q === 'string') {
        const b = document.createElement('button');
        b.textContent = tr(q);
        // 비한국어면 번역된 질문을 전송해 서버가 해당 언어로 답변.
        b.onclick = () => send(tr(q));
        sugg.appendChild(b);
        continue;
      }
      const title = q.title ?? '';
      const message = q.sendMessage ?? title;
      const preset = q.presetAnswer || q.presetBlocks
        ? { answer: q.presetAnswer ?? '', image: q.presetImage, blocks: q.presetBlocks }
        : null;
      // 프리셋은 원문 title로(정확 매칭 필요), 일반 전송은 번역 메시지로.
      const trigger = preset ? () => sendPreset(title, preset) : () => send(tr(message));
      if (!isRich(q)) {
        const b = document.createElement('button');
        b.textContent = tr(title);
        b.onclick = trigger;
        sugg.appendChild(b);
        continue;
      }
      const card = document.createElement('button');
      card.className = 'cbsa-card';
      card.onclick = trigger;
      if (q.imageUrl) {
        const img = document.createElement('img');
        img.className = 'cbsa-card-img';
        img.src = q.imageUrl;
        img.alt = '';
        img.loading = 'lazy';
        img.decoding = 'async';
        img.onerror = () => img.remove();
        card.appendChild(img);
      }
      const body = document.createElement('div');
      body.className = 'cbsa-card-body';
      const tEl = document.createElement('strong');
      tEl.className = 'cbsa-card-title';
      tEl.textContent = tr(title);
      body.appendChild(tEl);
      if (q.description) {
        const d = document.createElement('span');
        d.className = 'cbsa-card-desc';
        d.textContent = tr(q.description);
        body.appendChild(d);
      }
      card.appendChild(body);
      sugg.appendChild(card);
    }
  }
  renderSuggestions();

  // 추천질문 전체 재빌드(번역 반영). switchLang이 번역 준비 후 호출.
  function rerenderSuggestions() {
    renderSuggestions();
    // 접힘/펼침 상태의 라벨(t(...) 기반)도 현재 언어로 갱신.
    applySuggCollapsed();
  }

  // ---- 언어 전환 ----
  async function switchLang(newLang: WLang) {
    if (newLang === lang) return;
    lang = newLang;
    try { localStorage.setItem(langKey, lang); } catch { /* ignore */ }
    t = makeT(lang);

    // 정적 크롬 문구 즉시 갱신(번역 불필요 — i18n 사전 기반).
    input.placeholder = t('inputPlaceholder');
    sendBtn.textContent = t('send');
    badgeTextNode.textContent = t('badge');
    closeBtn.setAttribute('aria-label', t('closeAria'));
    langSel.setAttribute('aria-label', t('langLabel'));
    if (button) button.setAttribute('aria-label', panel.style.display !== 'none' ? t('closeAria') : t('openAria'));
    if (launcherLabelEl) launcherLabelEl.textContent = launcherText();
    applySuggCollapsed(); // suggToggle 라벨(펼치기/숨기기)
    paintStatus();
    paintResume();
    langSel.value = lang;

    // ko는 원문 즉시 반영(번역 fetch 불필요).
    if (lang === 'ko') {
      hG.textContent = headerSubSrc;
      hN.textContent = headerTitleSrc();
      if (greetingBubbleEl) renderTextWithTelLinks(greetingBubbleEl, cfg.greeting);
      rerenderSuggestions();
      return;
    }

    // 이미 캐시된 번역으로 먼저 반영(즉시성) 후, 부족분 fetch되면 다시 반영.
    const paint = () => {
      hG.textContent = tr(headerSubSrc);
      hN.textContent = tr(headerTitleSrc());
      if (greetingBubbleEl) renderTextWithTelLinks(greetingBubbleEl, tr(cfg.greeting));
      rerenderSuggestions();
    };
    paint();
    await ensureTranslations(lang);
    // fetch 중 손님이 또 언어를 바꿨다면 덮어쓰지 않는다.
    if (lang === newLang) paint();
  }

  // 초기 언어가 비한국어(저장된 선택/테넌트 override)면 부족한 번역을 미리 받아 다시 그린다.
  if (lang !== 'ko') {
    const startLang = lang;
    void (async () => {
      await ensureTranslations(startLang);
      if (lang !== startLang) return;
      hG.textContent = tr(headerSubSrc);
      hN.textContent = tr(headerTitleSrc());
      if (greetingBubbleEl) renderTextWithTelLinks(greetingBubbleEl, tr(cfg.greeting));
      rerenderSuggestions();
    })();
  }

  // 손님 흐름 퍼널의 맨 앞 두 칸(위젯이 보였다 → 열었다). 페이지마다 처음 연 한 번만 센다 —
  // 닫았다 다시 연 것까지 세면 '연 사람'이 아니라 '여닫은 횟수'가 된다.
  let openTracked = false;
  const pagePath = () => { try { return location.pathname.slice(0, 120); } catch { return ''; } };
  // 휴대폰 키보드 — 전체 화면 패널을 **실제로 보이는 높이**에 맞춘다. iOS 사파리는 키보드가 올라와도
  // 레이아웃 높이(100vh)를 줄이지 않아 맨 아래 입력창이 키보드 뒤로 숨는다. visualViewport 가 그 높이를 안다.
  // 좁은 화면(전체 화면 시트)에서 패널이 열려 있을 때만 건드리고, 아니면 CSS 로 되돌린다.
  const narrow = typeof window.matchMedia === 'function' ? window.matchMedia('(max-width: 480px)') : null;
  // ⚠️ 되돌릴 때 **지우지 말고 원래 인라인 값으로** 돌린다. 데스크톱 패널의 자리(top·bottom)는
  // panelPositionStyle 인라인이 잡는다 — 지우면 static 자리(페이지 맨 끝)로 떨어져 열어도 안 보인다
  // (2026-10-09 실측: staytoq.com 에서 패널이 y=14133 에 열렸다).
  const FIT_PROPS = ['height', 'max-height', 'top', 'bottom'] as const;
  let fitSaved: [string, string, string][] | null = null;
  function fitToViewport() {
    const vv = window.visualViewport;
    const on = !isInline && !!narrow?.matches && !!vv && panel.style.display !== 'none';
    if (!on || !vv) {
      if (fitSaved) {
        for (const [p, v, prio] of fitSaved) {
          if (v) panel.style.setProperty(p, v, prio); else panel.style.removeProperty(p);
        }
        fitSaved = null;
      }
      return;
    }
    fitSaved ??= FIT_PROPS.map((p) => [p, panel.style.getPropertyValue(p), panel.style.getPropertyPriority(p)]);
    panel.style.setProperty('height', `${Math.round(vv.height)}px`, 'important');
    panel.style.setProperty('max-height', `${Math.round(vv.height)}px`, 'important');
    panel.style.setProperty('top', `${Math.round(vv.offsetTop)}px`, 'important');
    panel.style.setProperty('bottom', 'auto', 'important');
  }
  window.visualViewport?.addEventListener('resize', fitToViewport);
  window.visualViewport?.addEventListener('scroll', fitToViewport);
  narrow?.addEventListener?.('change', fitToViewport);

  function setOpen(open: boolean) {
    const wasOpen = panel.style.display !== 'none';
    if (open && !wasOpen && !openTracked) { openTracked = true; trackEvent('panel_open', { path: pagePath() }); }
    // 숨기기 전에 잰다 — 숨긴 뒤엔 포커스가 이미 body 로 빠져 있다.
    const focusInside = panel.contains(document.activeElement);
    panel.style.display = open ? 'flex' : 'none';
    fitToViewport();
    if (open) {
      hideTeasers();
      // 입력창이 아니라 창 자체로 — 휴대폰에서 여는 순간 키패드가 뜨지 않게.
      requestAnimationFrame(() => panel.focus({ preventScroll: true }));
    }
    if (button) {
      button.classList.toggle('is-open', open);
      button.setAttribute('aria-label', open ? t('closeAria') : t('openAria'));
      button.setAttribute('aria-expanded', String(open));
      if (!open && focusInside) button.focus();
    }
  }
  if (button) button.onclick = () => setOpen(panel.style.display === 'none');
  // 위젯이 손님 화면에 떴다 — 방문자당 하루 한 번만(새로고침마다 세면 노출이 부풀고 요청만 는다).
  if (button) {
    try {
      const k = `cbsa_view_${opts.tenantSlug}`, d = new Date().toISOString().slice(0, 10);
      if (localStorage.getItem(k) !== d) { localStorage.setItem(k, d); trackEvent('widget_view', { path: pagePath() }); }
    } catch { /* 저장소를 못 쓰면 세지 않는다 */ }
  }
  closeBtn.onclick = () => setOpen(false);
  sendBtn.onclick = () => {
    if (input.value.trim()) queueSend(input.value.trim());
  };
  input.onkeydown = (e) => {
    // Korean/Japanese/Chinese IME: Enter while composing must not submit, or
    // the in-progress jamo gets sent as its own message after the form clears.
    if (e.key !== 'Enter') return;
    if (e.isComposing || (e as unknown as { keyCode?: number }).keyCode === 229) return;
    e.preventDefault();
    sendBtn.click();
  };

  function appendMsg(role: 'user' | 'assistant', text: string): HTMLDivElement {
    const el = document.createElement('div');
    el.className = `cbsa-msg ${role}`;
    renderTextWithTelLinks(el, text);
    msgs.appendChild(el);
    msgs.scrollTop = msgs.scrollHeight;
    return el;
  }

  // -------- 예약 링크 클릭 추적(예약 유도 성공 = 전환 신호) --------
  function isReservationUrl(href: string): boolean {
    try {
      const u = new URL(href, location.href);
      return /(^|\.)booking\.naver\.com$/.test(u.hostname) || u.hostname === 'naver.me' || u.hostname.endsWith('.naver.me');
    } catch { return false; }
  }
  function trackReservationClick(url: string) {
    try {
      fetch(`${opts.apiBase}/api/widget-track`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        keepalive: true,
        body: JSON.stringify({ tenantSlug: opts.tenantSlug, visitorId, eventType: 'reservation_click', payload: { url } }),
      }).catch(() => {});
    } catch { /* ignore */ }
  }
  // 대화 영역의 예약 CTA(data-resv) 또는 예약 도메인 링크 클릭을 위임으로 포착.
  msgs.addEventListener('click', (e) => {
    const a = (e.target as HTMLElement)?.closest?.('a') as HTMLAnchorElement | null;
    if (!a) return;
    const href = a.getAttribute('href') || '';
    if (a.dataset.resv === '1' || isReservationUrl(href)) trackReservationClick(href);
  }, true);

  // -------- 이전 대화(과거 히스토리) --------
  const DAY_LOCALE: Record<WLang, string> = { ko: 'ko-KR', en: 'en-US', ja: 'ja-JP', zh: 'zh-CN' };
  function dayKeyOf(d: Date): string {
    // KST 기준 YYYY-MM-DD (일 경계는 한국 시간으로 판정)
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
  }
  function dayLabel(iso: string): string {
    const key = dayKeyOf(new Date(iso));
    if (key === dayKeyOf(new Date())) return t('today');
    if (key === dayKeyOf(new Date(Date.now() - 86_400_000))) return t('yesterday');
    try {
      return new Intl.DateTimeFormat(DAY_LOCALE[lang] ?? 'ko-KR', { timeZone: 'Asia/Seoul', month: 'long', day: 'numeric' }).format(new Date(iso));
    } catch { return key; }
  }
  function makeDayDivider(label: string): HTMLDivElement {
    const d = document.createElement('div');
    d.className = 'cbsa-day';
    const s = document.createElement('span');
    s.textContent = label;
    d.appendChild(s);
    return d;
  }

  async function loadPreviousConversation(): Promise<void> {
    if (historyLoaded) return;
    // 헤더 버튼으로 불러와도 카드는 할 일이 끝났다.
    resumeCard?.remove();
    resumeCard = null;
    const orig = historyLabelEl.textContent;
    historyBtn.disabled = true;
    historyLabelEl.textContent = t('historyLoading');
    historyBtn.title = t('historyLoading');
    let past: { role: 'user' | 'assistant'; content: string; createdAt: string }[] = [];
    try {
      const res = await fetch(`${opts.apiBase}/api/widget-history`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantSlug: opts.tenantSlug, visitorId }),
      });
      const json = await res.json().catch(() => null);
      past = Array.isArray(json?.messages) ? json.messages : [];
    } catch {
      historyLabelEl.textContent = t('historyErr');
      historyBtn.title = t('historyErr');
      setTimeout(() => { historyLabelEl.textContent = orig; historyBtn.title = orig ?? ''; historyBtn.disabled = false; }, 2000);
      return;
    }
    if (!past.length) {
      historyLabelEl.textContent = t('historyEmpty');
      historyBtn.title = t('historyEmpty');
      setTimeout(() => { historyLabelEl.textContent = orig; historyBtn.title = orig ?? ''; historyBtn.disabled = false; }, 2000);
      return;
    }
    historyLoaded = true;
    // 과거 메시지를 메시지 영역 맨 위에 삽입 — 날짜별 구분선으로 묶는다.
    // 과거 메시지는 저장 당시 언어 원문 그대로 표시(대화 나머지는 원문 유지 정책과 일치).
    const frag = document.createDocumentFragment();
    frag.appendChild(makeDayDivider(t('historyLabel')));
    let lastKey = '';
    for (const m of past) {
      if (m.role !== 'user' && m.role !== 'assistant') continue;
      const key = dayKeyOf(new Date(m.createdAt));
      if (key !== lastKey) { lastKey = key; frag.appendChild(makeDayDivider(dayLabel(m.createdAt))); }
      const el = document.createElement('div');
      el.className = `cbsa-msg ${m.role}`;
      renderTextWithTelLinks(el, m.content);
      frag.appendChild(el);
    }
    // 현재 세션(인사말) 앞에 '오늘' 구분선 — 마지막 과거 메시지가 이미 오늘이면 생략.
    if (lastKey !== dayKeyOf(new Date())) frag.appendChild(makeDayDivider(t('today')));
    msgs.insertBefore(frag, msgs.firstChild);
    msgs.scrollTop = 0; // 손님이 불러온 과거의 시작부터 읽도록
    historyBtn.remove();
  }

  function showTypingIndicator(target: HTMLDivElement) {
    while (target.firstChild) target.removeChild(target.firstChild);
    const wrap = document.createElement('span');
    wrap.className = 'cbsa-typing';
    wrap.setAttribute('aria-label', t('typing'));
    for (let i = 0; i < 3; i++) wrap.appendChild(document.createElement('span'));
    target.appendChild(wrap);
  }

  // 처리 단계 한 줄 — 규칙은 stage-line.ts. 문구는 손님 언어로.
  const STAGE_LABEL: Record<string, WKey> = {
    intent: 'stageIntent', history: 'stageHistory', prepare: 'stagePrepare',
    knowledge: 'stageKnowledge', availability: 'stageAvailability',
  };
  const stageLine = (target: HTMLDivElement) =>
    createStageLine(target, stage => (STAGE_LABEL[stage] ? t(STAGE_LABEL[stage]) : null));

  // Preset answer: skip the API entirely and render the static reply that the
  // operator pre-wrote in admin. We still simulate the chatbot UX — typing
  // dots, brief preparation pause, then the text flows in one chunk at a time
  // so the user reads top-to-bottom instead of being dropped at the end of a
  // long reply. The image (if set) is prepended once so it appears before the
  // text starts streaming.
  function trackEvent(eventType: 'preset_click' | 'action_click' | 'panel_open' | 'widget_view' | 'history_resume', payload?: Record<string, unknown>) {
    // Fire-and-forget — never block UI on telemetry. Failures swallowed.
    try {
      fetch(`${opts.apiBase}/api/widget-track`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        keepalive: true,
        body: JSON.stringify({
          tenantSlug: opts.tenantSlug,
          visitorId: visitorId,
          eventType,
          payload: payload ?? {},
        }),
      }).catch(() => {});
    } catch { /* ignore */ }
  }

  async function sendPreset(
    triggerText: string,
    preset: { answer: string; image?: string; blocks?: PresetBlock[] },
  ) {
    trackEvent('preset_click', { title: triggerText, hasBlocks: !!preset.blocks?.length });
    appendMsg('user', triggerText);
    history.push({ role: 'user', content: triggerText });
    const bubble = appendMsg('assistant', '');
    showTypingIndicator(bubble);
    msgs.scrollTop = msgs.scrollHeight;

    await new Promise(r => setTimeout(r, 600));

    // Rich block path: render structured cards/lists/actions one at a time.
    if (preset.blocks?.length) {
      bubble.replaceChildren();
      bubble.classList.add('cbsa-msg-blocks');
      const scrollToBottom = () => { msgs.scrollTop = msgs.scrollHeight; };
      for (const block of preset.blocks) {
        const el = renderBlock(block);
        bubble.appendChild(el);
        scrollToBottom();
        // Images come in async — re-scroll once each one's intrinsic size lands
        // so the latest content stays in view as the bubble grows.
        el.querySelectorAll('img').forEach(img => {
          if (!(img as HTMLImageElement).complete) {
            img.addEventListener('load', scrollToBottom, { once: true });
            img.addEventListener('error', scrollToBottom, { once: true });
          }
        });
        await new Promise(r => setTimeout(r, 220));
      }
      // After the whole message is laid out, bring the start of the bubble
      // into view so the user reads from the hero image down, not from a
      // mid-card position the bottom-anchor scroll happened to land on.
      requestAnimationFrame(() => {
        bubble.scrollIntoView({ block: 'start', behavior: 'smooth' });
      });
      history.push({ role: 'assistant', content: '[rich blocks]' });
      announce(bubble);
      return;
    }

    // Plain text path (existing): optional image then char-streamed text.
    let acc = preset.image ? `![](${preset.image})\n\n` : '';
    if (preset.image) {
      renderTextWithTelLinks(bubble, acc);
      msgs.scrollTop = msgs.scrollHeight;
      await new Promise(r => setTimeout(r, 250));
    }
    const text = preset.answer;
    const chunkSize = text.length > 400 ? 2 : 1;
    const delay = 22;
    for (let i = 0; i < text.length; i += chunkSize) {
      acc += text.slice(i, i + chunkSize);
      renderTextWithTelLinks(bubble, acc);
      msgs.scrollTop = msgs.scrollHeight;
      await new Promise(r => setTimeout(r, delay));
    }
    history.push({ role: 'assistant', content: acc });
    announce(bubble);
  }

  // -------- block renderers --------
  function renderBlock(block: PresetBlock): HTMLElement {
    if (block.type === 'image') {
      const img = document.createElement('img');
      img.src = block.url;
      img.alt = block.alt ?? '';
      img.className = 'cbsa-block-image';
      img.loading = 'lazy';
      img.decoding = 'async';
      img.onerror = () => img.remove();
      return img;
    }
    if (block.type === 'text') {
      const div = document.createElement('div');
      div.className = 'cbsa-block-text';
      renderTextWithTelLinks(div, block.text);
      return div;
    }
    if (block.type === 'list') {
      const wrap = document.createElement('div');
      wrap.className = 'cbsa-block-list';
      for (const it of block.items) {
        const card = document.createElement('div');
        card.className = 'cbsa-list-item';
        const body = document.createElement('div');
        body.className = 'cbsa-list-body';
        const t = document.createElement('strong');
        t.className = 'cbsa-list-title';
        t.textContent = it.title;
        body.appendChild(t);
        if (it.description) {
          const d = document.createElement('div');
          d.className = 'cbsa-list-desc';
          renderTextWithTelLinks(d, it.description);
          body.appendChild(d);
        }
        if (it.buttonLabel && it.buttonUrl) {
          const a = document.createElement('a');
          a.className = 'cbsa-list-button';
          a.href = it.buttonUrl;
          a.target = '_blank';
          a.rel = 'noopener noreferrer';
          a.textContent = `${it.buttonLabel} ›`;
          body.appendChild(a);
        }
        card.appendChild(body);
        if (it.thumbnail) {
          const thumb = document.createElement('img');
          thumb.className = 'cbsa-list-thumb';
          thumb.src = it.thumbnail;
          thumb.alt = '';
          thumb.loading = 'lazy';
          thumb.decoding = 'async';
          thumb.onerror = () => thumb.remove();
          card.appendChild(thumb);
        }
        wrap.appendChild(card);
      }
      return wrap;
    }
    // actions
    const wrap = document.createElement('div');
    wrap.className = 'cbsa-block-actions';
    for (const it of block.items) {
      const btn = document.createElement(it.url ? 'a' : 'button');
      btn.className = 'cbsa-action-btn';
      if (it.url && btn instanceof HTMLAnchorElement) {
        btn.href = it.url;
        btn.target = '_blank';
        btn.rel = 'noopener noreferrer';
        btn.addEventListener('click', () => {
          trackEvent('action_click', { label: it.label, url: it.url });
        });
      } else if (btn instanceof HTMLButtonElement) {
        btn.type = 'button';
        btn.onclick = () => {
          trackEvent('action_click', { label: it.label, presetRef: it.presetRef, chipTrigger: it.chipTrigger });
          // Priority: presetRef (re-trigger another chip's preset) → chipTrigger (RAG send)
          if (it.presetRef) {
            const target = (cfg.suggestedQuestions ?? []).find(
              (sq): sq is Exclude<SuggestedQuestion, string> =>
                typeof sq === 'object' && sq.title === it.presetRef
            );
            if (target && (target.presetAnswer || target.presetBlocks)) {
              sendPreset(target.title, {
                answer: target.presetAnswer ?? '',
                image: target.presetImage,
                blocks: target.presetBlocks,
              });
              return;
            }
          }
          if (it.chipTrigger) send(it.chipTrigger);
        };
      }
      btn.textContent = `${it.label} ›`;
      wrap.appendChild(btn);
    }
    return wrap;
  }

  // 손님 피드백(👍/👎) 전송 — messages.feedback 온라인 신호. 실패해도 조용히 무시.
  function sendFeedback(messageId: string, value: 1 | -1 | 0) {
    try {
      fetch(`${opts.apiBase}/api/widget-feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantSlug: opts.tenantSlug, messageId, value, visitorId }),
        keepalive: true,
      }).catch(() => {});
    } catch { /* noop */ }
  }

  // 봇 답변 말풍선 아래에 만족/불만족 버튼을 붙인다. 클릭 시 서버에 기록하고,
  // 같은 버튼 재클릭은 취소(토글). 스몰톡·짧은 안내엔 미노출.
  function attachFeedback(bubble: HTMLElement, messageId: string) {
    const fb = document.createElement('div');
    fb.className = 'cbsa-fb';
    const q = document.createElement('span');
    q.className = 'cbsa-fb-q';
    q.textContent = t('feedbackAsk');
    fb.appendChild(q);
    const mkBtn = (value: 1 | -1, up: boolean, label: string) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'cbsa-fb-btn';
      b.setAttribute('aria-label', label);
      b.title = label;
      b.appendChild(makeThumbSvg(up));
      b.addEventListener('click', () => {
        const wasOn = b.classList.contains('cbsa-fb-on');
        fb.querySelectorAll('.cbsa-fb-btn').forEach(x => x.classList.remove('cbsa-fb-on'));
        if (!wasOn) b.classList.add('cbsa-fb-on');
        sendFeedback(messageId, wasOn ? 0 : value);
      });
      return b;
    };
    fb.appendChild(mkBtn(1, true, t('feedbackYes')));
    fb.appendChild(mkBtn(-1, false, t('feedbackNo')));
    bubble.appendChild(fb);
  }

  // 웹 위젯 클라이언트 디바운스 — 손님이 한 생각을 여러 줄로 빠르게 보내면 조각마다
  // 요청하지 않고 조용해질 때까지 모았다가 합쳐 한 번에 전송(네이버 서버 디바운스의 웹판).
  // 말풍선은 입력 즉시 표시해 UX를 유지하고 요청만 지연한다.
  // 대기 시간 판정은 `./debounce` 한 곳에 있다 — 테스트가 가능해야 해서 뺐다.
  let pendingTexts: string[] = [];
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  function queueSend(text: string) {
    input.value = '';
    appendMsg('user', text);
    msgs.scrollTop = msgs.scrollHeight;
    pendingTexts.push(text);
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(flushSend, debounceMsFor(text));
  }
  function flushSend() {
    debounceTimer = null;
    if (!pendingTexts.length) return;
    const combined = pendingTexts.join('\n');
    pendingTexts = [];
    history.push({ role: 'user', content: combined });
    void streamAnswer(combined);
  }

  // 추천질문 등 즉시 전송(디바운스 없음).
  async function send(text: string) {
    input.value = '';
    appendMsg('user', text);
    history.push({ role: 'user', content: text });
    await streamAnswer(text);
  }

  // 합쳐진 발화 1건을 서버로 스트리밍. 손님 말풍선/히스토리는 호출부가 이미 처리.
  async function streamAnswer(message: string) {
    // 서버에 남는 질문만 표시한다 — 추천 질문의 고정 답(sendPreset)은 서버 대화를 만들지 않아
    // 다음에 '이어보기'를 눌러도 빈 화면이 된다.
    try { localStorage.setItem(resumeKey, buildResumeMarker(message, Date.now())); } catch { /* ignore */ }
    const bubble = appendMsg('assistant', '');
    showTypingIndicator(bubble);
    msgs.scrollTop = msgs.scrollHeight;

    // ── 어드민 데이터 질의 브리지 ──────────────────────────────────
    // 사장님이 도우미에게 "이번 달 매출 얼마야?" 라고 물으면, 어드민 페이지가 등록한
    // 훅이 **인증된** 지표 API 로 답을 가져온다. 매칭되면 여기서 끝내고 /api/chat 을
    // 아예 부르지 않는다 — 운영 수치가 공용 답변 캐시나 대화 기록에 섞이면 나중에
    // 다른 질문이 그걸 히트해 손님에게 나갈 수 있다(개인 답변은 공유 캐시를 비킨다).
    if (opts.adminBridge) {
      const ask = (window as unknown as { __staytoqAdminAsk?: AdminAskFn }).__staytoqAdminAsk;
      if (typeof ask === 'function') {
        try {
          const r = await ask(message);
          if (r && r.matched && r.answer) {
            bubble.replaceChildren();
            renderTextWithTelLinks(bubble, r.answer);
            if (r.href) appendMoreLink(bubble, r.href, r.linkLabel ?? '자세히 보기');
            // **운영 수치는 history 에 넣지 않는다.** history 는 다음 턴에 /api/chat 으로
            // 그대로 전송되므로, 매출을 담으면 공용 도우미 테넌트의 프롬프트·대화기록에
            // 섞이고 거기서 만들어진 답이 캐시되면 다른 사람 질문이 그걸 히트할 수 있다.
            // 역할 교대만 유지되도록 자리표시자를 넣는다(내용 없음).
            history.push({ role: 'assistant', content: '(데이터 질의로 답변함)' });
            announce(bubble);
            msgs.scrollTop = msgs.scrollHeight;
            return;
          }
        } catch { /* 브리지 실패는 무시 — 평소대로 도우미가 답한다 */ }
      }
    }

    let acc = '';
    let typingShown = true;
    const stages = stageLine(bubble);
    await streamChat({
      apiBase: opts.apiBase,
      tenantSlug: opts.tenantSlug,
      visitorId: visitorId!,
      conversationId,
      history: history.slice(-10),
      message,
      lang,
      phone: cfg.phone,
      onStage: (s) => stages.push(s),
      onToken: (t) => {
        acc += t;
        stages.stop();
        if (typingShown) typingShown = false;
        renderTextWithTelLinks(bubble, acc);
        msgs.scrollTop = msgs.scrollHeight;
      },
      // 서버가 '입금 확인했습니다' 같은 거짓 단정을 고쳐 전문을 다시 보내면 말풍선을 바꿔 끼운다.
      // acc 도 바꿔야 다음 턴 history 와 예약 버튼 판정이 고친 글을 본다.
      onReplace: (text) => {
        acc = text;
        stages.stop();
        typingShown = false;
        renderTextWithTelLinks(bubble, acc);
      },
      onDone: (info) => {
        stages.stop();
        conversationId = info.conversationId;
        if (conversationId) {
          localStorage.setItem(convKey, conversationId);
          localStorage.setItem(convTsKey, String(Date.now()));
        }
        // Defensive: if the stream finished with no tokens at all (e.g. an
        // upstream error returning done immediately) wipe the typing dots so
        // the bubble doesn't sit forever animating.
        if (typingShown) bubble.replaceChildren();
        history.push({ role: 'assistant', content: acc });
        announce(bubble);

        // 예약 버튼·사장님 연결 카드 — 판정과 본문 정리는 채널 공용 규격(resolveReply)이 한다.
        // 버튼 URL 은 언제나 서버(config) 값이라 모델이 URL 을 변형해도 링크는 안전하다.
        // 새 탭 + rel 보호, 라벨은 textContent(안전).
        // 행동 목록은 서버 것(actions 이벤트)이 우선이다 — 서버는 후처리를 마친 최종 글로 정한다.
        // 본문에서 링크를 걷는 것은 여기서 받은 글(acc)에 한다.
        const reply = resolveReply(acc, info);
        // 단, 서버 후처리가 링크 든 맺음 문장을 걷어 서버 목록엔 예약이 없는데 여기 글엔 링크가
        // 남은 경우가 있을 수 있다 — 그땐 여기서 찾은 예약 버튼을 더한다(맨 URL 이 남지 않게).
        const localResv = reply.actions.find(a => a.kind === 'reservation');
        const actions = info.actions
          ? (localResv && !info.actions.some(a => a.kind === 'reservation') ? [localResv, ...info.actions] : info.actions)
          : reply.actions;
        for (const act of actions) {
          if (act.kind !== 'reservation') continue;
          renderTextWithTelLinks(bubble, reply.text || t('bookLead'));
          const cta = document.createElement('a');
          cta.href = act.url;
          cta.target = '_blank';
          cta.rel = 'noopener noreferrer';
          cta.dataset.resv = '1'; // 예약 링크 클릭 추적(전환 신호) — 위임 리스너가 포착

          // 대화로 슬롯(날짜·객실·인원)이 잡혔으면 미니 예약 카드로 승격(요약 + '예약하러 가기').
          // 그 외엔 기존 단순 버튼.
          if (act.summary) {
            const card = document.createElement('div');
            card.className = 'cbsa-book-card';
            const title = document.createElement('div');
            title.className = 'cbsa-book-title';
            title.textContent = t('bookCardTitle');
            const sum = document.createElement('div');
            sum.className = 'cbsa-book-sum';
            sum.textContent = act.summary;
            cta.className = 'cbsa-cta-btn cbsa-book-cta';
            // 화살표는 .cbsa-cta-btn::after 가 붙이므로 라벨만.
            cta.textContent = act.label && act.label !== '예약하기' ? act.label : t('ctaConfirm');
            card.appendChild(title);
            card.appendChild(sum);
            card.appendChild(cta);
            bubble.appendChild(card);
          } else {
            cta.className = 'cbsa-cta-btn';
            cta.textContent = act.label || t('ctaDefault');
            bubble.appendChild(cta);
          }
          msgs.scrollTop = msgs.scrollHeight;
        }

        // 상담 안전 분기(핸드오프) 연결 카드 — 전화=tel: 버튼, 문의폼=http(s) 링크만.
        const handoffCard = document.createElement('div');
        handoffCard.className = 'cbsa-handoff-card';
        for (const act of actions) {
          if (act.kind === 'call') {
            const a = document.createElement('a');
            a.href = `tel:${act.tel}`;
            a.className = 'cbsa-handoff-btn';
            a.textContent = `${t('handoffCall')} · ${act.phone}`;
            handoffCard.appendChild(a);
          } else if (act.kind === 'contact') {
            const a = document.createElement('a');
            a.href = act.url;
            a.target = '_blank';
            a.rel = 'noopener noreferrer';
            a.className = 'cbsa-handoff-btn cbsa-handoff-ghost';
            a.textContent = t('handoffInquiry');
            handoffCard.appendChild(a);
          }
        }
        if (handoffCard.childElementCount > 0) { bubble.appendChild(handoffCard); msgs.scrollTop = msgs.scrollHeight; }

        // 다음 질문 — 서버가 사장님 추천 질문 칩에서 고른 것(follow-ups.ts). 프리셋이 있으면
        // LLM 없이 바로 답하고, 없으면 손님 말로 보낸다. 프리셋 안 버튼(presetRef)과 같은 모양.
        const asks = actions.filter((a): a is Extract<typeof a, { kind: 'ask' }> => a.kind === 'ask');
        if (asks.length > 0) {
          const row = document.createElement('div');
          row.className = 'cbsa-block-actions cbsa-followups';
          for (const a of asks) {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'cbsa-action-btn';
            b.textContent = `${tr(a.text)} ›`;
            b.onclick = () => {
              trackEvent('action_click', { label: a.text, followUp: true });
              const target = (cfg.suggestedQuestions ?? []).find(
                (sq): sq is Exclude<SuggestedQuestion, string> =>
                  typeof sq === 'object' && sq.title === a.text && !!(sq.presetAnswer || sq.presetBlocks)
              );
              if (target) sendPreset(target.title, { answer: target.presetAnswer ?? '', image: target.presetImage, blocks: target.presetBlocks });
              else send(tr(a.text));
            };
            row.appendChild(b);
          }
          bubble.appendChild(row);
          msgs.scrollTop = msgs.scrollHeight;
        }

        // 손님 피드백(👍/👎) — 실제 답변 턴에만(스몰톡·빈 답변 제외). 온라인 신호로
        // '답변 개선' 루프에 주입된다(👎 = 최우선 개선 후보).
        if (info.messageId && !info.smalltalk && acc.trim().length >= 8) {
          attachFeedback(bubble, info.messageId);
          msgs.scrollTop = msgs.scrollHeight;
        }
      },
      onError: () => {
        stages.stop();
        bubble.textContent = t('bubbleError');
      },
    });
  }
}
